import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { workOrdersApi, tecniciApi, committentiApi, rapportiniApi, magazzinoApi, magazzinoApi2, checklistApi, orariAperturaApi, kitApi, clientiFinaliApi } from '../services/api'
import {
  saveRapportiniOffline,
  getPendingRapportini,
  deleteRapportiniOffline,
  blobToArrayBuffer,
  arrayBufferToBlob,
  type RapportiniOffline,
} from '../lib/offlineRapportini'
import { compressImage } from '../lib/imageCompress'
import { StatoBadge, PrioritaBadge, TipologiaBadge } from '../components/StatusBadge'
import { AppuntamentoChip } from '../components/AppuntamentoChip'
import { ArrowLeft, User, MapPin, Calendar, CheckCircle, PauseCircle, XCircle, FileText, Download, Pencil, Clock, CheckSquare, Archive } from 'lucide-react'
import { format } from 'date-fns'
import { it } from 'date-fns/locale'
import toast from 'react-hot-toast'
import { useState, useRef, useEffect, useCallback } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useTemplateRapportino } from '../hooks/useTemplateRapportino'
import CampoRapportino from '../components/CampoRapportino'

// ─── Stati visibili all'utente ────────────────────────────────────────────
const STATI_UI = [
  { key: 'ricevuto',  label: 'Ricevuto',  color: '#64748B' },
  { key: 'assegnato', label: 'Assegnato', color: '#3B82F6' },
  { key: 'sospeso',   label: 'Sospeso',   color: '#F59E0B' },
  { key: 'annullato', label: 'Annullato', color: '#EF4444' },
  { key: 'eseguito',  label: 'Eseguito',  color: '#10B981' },
]

// Mappa frontend → backend
const STATO_TO_BACKEND: Record<string, string> = {
  ricevuto: 'ricevuto', assegnato: 'assegnato',
  sospeso: 'sospeso', annullato: 'annullato', eseguito: 'eseguito',
}
// Mappa backend → frontend label
const STATO_FROM_BACKEND: Record<string, string> = {
  ricevuto: 'ricevuto', in_verifica: 'ricevuto', pianificato: 'assegnato',
  assegnato: 'assegnato', confermato_tecnico: 'assegnato', in_lavorazione: 'assegnato',
  completato: 'eseguito', in_validazione: 'eseguito', validato: 'eseguito',
  fatturabile: 'eseguito', fatturato: 'eseguito', chiuso: 'eseguito',
  annullato: 'annullato', sospeso: 'sospeso',
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-500)', fontFamily: "'Barlow Semi Condensed', sans-serif", marginBottom: 4 }}>
        {label}
      </p>
      <div style={{ fontSize: 13, color: 'var(--text-200)' }}>{children}</div>
    </div>
  )
}

function SideCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="card p-4">
      <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--signal)', fontFamily: "'Barlow Semi Condensed', sans-serif", marginBottom: 12 }}>
        {title}
      </p>
      {children}
    </div>
  )
}

// ─── Signature Canvas ─────────────────────────────────────────────────────
function SignatureCanvas({ label, onSign }: { label: string; onSign: (blob: Blob) => void }) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const drawing = useRef(false)
  const hasStrokes = useRef(false)
  const [open, setOpen] = useState(false)
  const [signed, setSigned] = useState(false)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  const getPos = (e: MouseEvent | TouchEvent, canvas: HTMLCanvasElement) => {
    const rect = canvas.getBoundingClientRect()
    const src = 'touches' in e ? e.touches[0] : e
    // scala le coordinate del touch in base alla risoluzione reale del canvas
    const scaleX = canvas.width / rect.width
    const scaleY = canvas.height / rect.height
    return { x: (src.clientX - rect.left) * scaleX, y: (src.clientY - rect.top) * scaleY }
  }

  const initCanvas = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    canvas.width = 340
    canvas.height = 160
    const ctx = canvas.getContext('2d')!
    ctx.fillStyle = '#fff'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    ctx.strokeStyle = '#1e293b'
    ctx.lineWidth = 3
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
  }, [])

  // Inizializza canvas quando l'overlay si apre
  useEffect(() => {
    if (!open) return
    // attende un frame per assicurarsi che il canvas sia nel DOM con le dimensioni corrette
    requestAnimationFrame(() => initCanvas())
  }, [open, initCanvas])

  useEffect(() => {
    if (!open) return
    const canvas = canvasRef.current
    if (!canvas) return

    const start = (e: MouseEvent | TouchEvent) => {
      e.preventDefault()
      drawing.current = true
      const ctx = canvas.getContext('2d')!
      const pos = getPos(e, canvas)
      ctx.beginPath()
      ctx.moveTo(pos.x, pos.y)
    }
    const move = (e: MouseEvent | TouchEvent) => {
      e.preventDefault()
      if (!drawing.current) return
      const ctx = canvas.getContext('2d')!
      const pos = getPos(e, canvas)
      ctx.lineTo(pos.x, pos.y)
      ctx.stroke()
      hasStrokes.current = true
    }
    const stop = (e: MouseEvent | TouchEvent) => {
      e.preventDefault()
      drawing.current = false
    }

    canvas.addEventListener('mousedown', start)
    canvas.addEventListener('mousemove', move)
    canvas.addEventListener('mouseup', stop)
    canvas.addEventListener('touchstart', start, { passive: false })
    canvas.addEventListener('touchmove', move, { passive: false })
    canvas.addEventListener('touchend', stop, { passive: false })
    return () => {
      canvas.removeEventListener('mousedown', start)
      canvas.removeEventListener('mousemove', move)
      canvas.removeEventListener('mouseup', stop)
      canvas.removeEventListener('touchstart', start)
      canvas.removeEventListener('touchmove', move)
      canvas.removeEventListener('touchend', stop)
    }
  }, [open])

  // Blocca scroll del body quando overlay è aperto
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => { document.body.style.overflow = '' }
  }, [open])

  const confirm = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas || !hasStrokes.current) return
    canvas.toBlob(b => {
      if (!b || b.size === 0) return
      onSign(b)
      setSigned(true)
      setPreviewUrl(canvas.toDataURL('image/png'))
      setOpen(false)
    }, 'image/png')
  }, [onSign])

  const clearCanvas = () => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')!
    ctx.fillStyle = '#fff'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    hasStrokes.current = false
  }

  const reset = () => {
    setSigned(false)
    setPreviewUrl(null)
    hasStrokes.current = false
    onSign(new Blob())
  }

  return (
    <div>
      {signed && previewUrl ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 8px', border: '1.5px solid #10B981', borderRadius: 6, background: '#f0fdf4', height: 36 }}>
          <span style={{ fontSize: 11, color: '#10b981', fontWeight: 700, flexShrink: 0 }}>✓</span>
          <img src={previewUrl} alt="firma" style={{ flex: 1, height: 28, objectFit: 'contain', minWidth: 0 }} />
          <span style={{ fontSize: 11, color: '#64748b', flexShrink: 0, fontWeight: 500 }}>{label.split('—')[0].trim()}</span>
          <button type="button" onClick={reset}
            style={{ flexShrink: 0, border: '1px solid #d1d5db', borderRadius: 4, padding: '1px 6px', fontSize: 10, cursor: 'pointer', color: '#64748b', background: '#fff' }}>
            ↺
          </button>
        </div>
      ) : (
        <button type="button" onClick={() => { hasStrokes.current = false; setOpen(true) }}
          style={{ width: '100%', height: 36, border: '1.5px dashed #94a3b8', borderRadius: 6,
            background: '#f8fafc', cursor: 'pointer', display: 'flex', alignItems: 'center',
            justifyContent: 'center', gap: 6, color: '#64748b', fontSize: 13 }}>
          <span>✍️</span>
          <span style={{ fontWeight: 500 }}>{label}</span>
        </button>
      )}

      {/* Overlay fullscreen per la firma */}
      {open && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: '#fff', display: 'flex', flexDirection: 'column',
        }}>
          {/* Header */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '10px 16px', borderBottom: '1px solid #e5e7eb',
            background: '#fff', flexShrink: 0,
          }}>
            <span style={{ fontWeight: 600, fontSize: 15, color: '#1e293b' }}>{label}</span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                onClick={clearCanvas}
                style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#f8fafc', fontSize: 13, cursor: 'pointer', color: '#64748b' }}
              >
                Cancella
              </button>
              <button
                type="button"
                onClick={confirm}
                style={{ padding: '6px 14px', borderRadius: 8, border: 'none', background: '#1E40AF', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
              >
                Conferma
              </button>
            </div>
          </div>

          {/* Area firma */}
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', padding: 16 }}>
            <div className="firma-canvas-wrap" style={{ position: 'relative', width: '100%', maxWidth: 340 }}>
              <p style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', color: '#cbd5e1', fontSize: 13, pointerEvents: 'none', userSelect: 'none', textAlign: 'center', margin: 0, zIndex: 0 }}>
                Firma qui
              </p>
              <canvas
                ref={canvasRef}
                width={340}
                height={160}
                style={{
                  border: '2px solid #2563eb',
                  borderRadius: 12,
                  background: '#f8fafc',
                  touchAction: 'none',
                  display: 'block',
                  width: '100%',
                  height: 160,
                  cursor: 'crosshair',
                  position: 'relative',
                  zIndex: 1,
                }}
              />
            </div>
          </div>

          {/* Footer */}
          <div style={{ padding: '8px 16px', borderTop: '1px solid #e5e7eb', background: '#fff', flexShrink: 0 }}>
            <button
              type="button"
              onClick={() => setOpen(false)}
              style={{ width: '100%', padding: '10px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#f8fafc', fontSize: 14, cursor: 'pointer', color: '#64748b' }}
            >
              Annulla
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Modal Eseguito (multi-step) ──────────────────────────────────────────
function EseguitoModal({ wo, onClose, onSuccess }: { wo: any; onClose: () => void; onSuccess: () => void }) {
  const { user } = useAuth()
  const [step, setStep] = useState(1)
  const [sending, setSending] = useState(false)
  const [sendProgress, setSendProgress] = useState<number | null>(null)
  const [isOnline, setIsOnline] = useState(navigator.onLine)
  const [pendingRapportini, setPendingRapportini] = useState<RapportiniOffline[]>([])
  const [syncingRapportini, setSyncingRapportini] = useState(false)
  const [syncProgress, setSyncProgress] = useState<number | null>(null)
  const isSyncing = useRef(false)
  const kitLoadingRef = useRef(false)
  const maxStep = 5

  const {
    template,
    loading: templateLoading,
    error: templateError,
    valori: templateValori,
    setValore: setTemplateValore,
    validaObbligatori,
    buildFormData,
  } = useTemplateRapportino(wo?.id ?? null)

  // WiFi specific
  const [isSopralluogo, setIsSopralluogo] = useState(false)
  const [numAp, setNumAp] = useState(0)
  const [apLocazioni, setApLocazioni] = useState(['', '', '', ''])
  const [apSeriali, setApSeriali] = useState(['', '', '', ''])

  // Step 1 — Materiali
  const [materialiSel, setMaterialiSel] = useState<Array<{ codice: string; descrizione: string; quantita: number; unita_misura: string }>>([])
  const [apparatiSel, setApparatiSel] = useState<Array<{ id: number; seriale: string; modello: string; marca: string; azione: string }>>([])
  const [kitSelezionatiIds, setKitSelezionatiIds] = useState<number[]>([])

  const { data: magData } = useQuery({
    queryKey: ['magazzino-furgone'],
    queryFn: () => magazzinoApi2.daFurgone().then(r => r.data),
  })

  const kitSulFurgone: any[] = magData?.kit ?? []
  const [ricercaFurgone, setRicercaFurgone] = useState('')
  const qFurg = ricercaFurgone.toLowerCase().trim()
  const kitFiltrati = kitSulFurgone.filter((k: any) =>
    k.attivo && (!qFurg ||
      k.nome.toLowerCase().includes(qFurg) ||
      k.descrizione?.toLowerCase().includes(qFurg) ||
      k.materiali?.some((m: any) => m.seriale?.toLowerCase().includes(qFurg) || m.nome?.toLowerCase().includes(qFurg))
    )
  )
  const apparatiFiltrati: any[] = (magData?.apparati ?? []).filter((ap: any) =>
    !qFurg || ap.seriale.toLowerCase().includes(qFurg) || ap.modello?.toLowerCase().includes(qFurg)
  )
  const articoliFiltrati: any[] = (magData?.articoli ?? []).filter((art: any) =>
    !qFurg || art.codice.toLowerCase().includes(qFurg) || art.descrizione?.toLowerCase().includes(qFurg)
  )

  // Creazione manuale apparati
  const [manualRows, setManualRows] = useState<{modello: string; seriale: string}[]>([{modello: 'fg40', seriale: ''}])
  const [salvaManualePending, setSalvaManualePending] = useState(false)
  const addManualRow = () => setManualRows(r => [...r, {modello: 'fg40', seriale: ''}])
  const removeManualRow = (i: number) => setManualRows(r => r.filter((_, idx) => idx !== i))
  const updateManualRow = (i: number, field: 'modello' | 'seriale', val: string) =>
    setManualRows(r => r.map((row, idx) => idx === i ? {...row, [field]: val} : row))
  const salvaApparatiManuali = async () => {
    const validi = manualRows.filter(r => r.seriale.trim())
    if (!validi.length) { toast.error('Inserisci almeno un seriale'); return }
    setSalvaManualePending(true)
    try {
      const creati: any[] = []
      for (const row of validi) {
        try {
          const res = await magazzinoApi.createApparato({
            seriale: row.seriale.trim().toUpperCase(),
            modello: row.modello.trim() || 'fg40',
            categoria: 'cpe',
            stato: 'in_magazzino_centrale',
          })
          creati.push(res.data)
        } catch (e: any) {
          const detail = e?.response?.data?.detail || ''
          if (detail.toLowerCase().includes('already') || detail.toLowerCase().includes('unico') || detail.toLowerCase().includes('unique')) {
            toast.error(`Seriale ${row.seriale} già presente in magazzino`)
          } else {
            toast.error(`Errore creazione ${row.seriale}: ${detail || 'errore sconosciuto'}`)
          }
        }
      }
      if (creati.length) {
        setApparatiSel(prev => [...prev, ...creati.map((a: any) => ({id: a.id, seriale: a.seriale, modello: a.modello || '', marca: a.marca || '', azione: 'installato'}))])
        setManualRows([{modello: 'fg40', seriale: ''}])
        toast.success(`${creati.length} apparato/i creato/i e aggiunto/i all'intervento`)
      }
    } finally {
      setSalvaManualePending(false)
    }
  }

  // Step 2 — Dettagli
  const [descrizione, setDescrizione] = useState('')
  const [note, setNote] = useState('')
  const [nomeCliente, setNomeCliente] = useState('')
  // Forza il campo vuoto dopo il mount per battere il browser autofill
  useEffect(() => { setNomeCliente('') }, [])

  // Step 4 — Foto (4 obbligatorie + 3 opzionali)
  const FOTO_LABELS = [
    'FOTO RACK PRE-INTERVENTO',
    'FOTO RACK POST-INTERVENTO',
    'FOTO APPARATI INSTALLATI',
    'FOTO ANTENNA',
    'FOTO SONDA FEM',
    'FOTO PANORAMICA CONTESTO',
    'ALTRA FOTO',
  ]
  const [foto, setFoto] = useState<Array<File | null>>([null, null, null, null, null, null, null])
  const [fotoUrls, setFotoUrls] = useState<Array<string | null>>([null, null, null, null, null, null, null])

  // Firma cliente canvas
  const firmaCliRef = useRef<Blob | null>(null)
  const [firmaCliOk, setFirmaCliOk] = useState(false)
  const firmaClienteRef = useRef<HTMLCanvasElement>(null)
  const firmaDrawing = useRef(false)
  const firmaHasStrokes = useRef(false)

  // Firma tecnico canvas (WiFi only)
  const firmaTecRef = useRef<Blob | null>(null)
  const [firmaTecOk, setFirmaTecOk] = useState(false)
  const firmaTecCanvasRef = useRef<HTMLCanvasElement>(null)
  const firmaTecDrawing = useRef(false)
  const firmaTecHasStrokes = useRef(false)

  const FIRMA_W = typeof window !== 'undefined' ? Math.min(window.innerWidth - 80, 380) : 320
  const FIRMA_H = 150

  const firmaStep = 5

  useEffect(() => {
    if (step !== firmaStep) return
    const initC = (canvas: HTMLCanvasElement | null) => {
      if (!canvas) return
      canvas.width = FIRMA_W; canvas.height = FIRMA_H
      const ctx = canvas.getContext('2d')!
      ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, FIRMA_W, FIRMA_H)
      ctx.strokeStyle = '#1e293b'; ctx.lineWidth = 3; ctx.lineCap = 'round'; ctx.lineJoin = 'round'
    }
    requestAnimationFrame(() => {
      initC(firmaClienteRef.current)
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step])

  const getCanvasPos = (e: React.MouseEvent | React.TouchEvent, canvas: HTMLCanvasElement) => {
    const rect = canvas.getBoundingClientRect()
    const src = 'touches' in e ? e.touches[0] : e
    const scaleX = canvas.width / rect.width
    const scaleY = canvas.height / rect.height
    return { x: (src.clientX - rect.left) * scaleX, y: (src.clientY - rect.top) * scaleY }
  }

  // ── Firma cliente handlers ──
  const startFirma = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault(); const canvas = firmaClienteRef.current; if (!canvas) return
    firmaDrawing.current = true
    const ctx = canvas.getContext('2d')!
    const pos = getCanvasPos(e, canvas); ctx.beginPath(); ctx.moveTo(pos.x, pos.y)
  }
  const disegnaFirma = (e: React.MouseEvent | React.TouchEvent) => {
    if (!firmaDrawing.current) return; e.preventDefault()
    const canvas = firmaClienteRef.current; if (!canvas) return
    const ctx = canvas.getContext('2d')!
    const pos = getCanvasPos(e, canvas); ctx.lineTo(pos.x, pos.y); ctx.stroke()
    firmaHasStrokes.current = true
  }
  const stopFirma = (e: React.MouseEvent | React.TouchEvent) => {
    if (!firmaDrawing.current) return; e.preventDefault(); firmaDrawing.current = false
    const canvas = firmaClienteRef.current; if (!canvas || !firmaHasStrokes.current) return
    canvas.toBlob(b => { if (!b || b.size === 0) return; firmaCliRef.current = b; setFirmaCliOk(true) }, 'image/png')
  }
  const cancellaFirma = () => {
    const canvas = firmaClienteRef.current; if (!canvas) return
    const ctx = canvas.getContext('2d')!
    ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, canvas.width, canvas.height)
    firmaHasStrokes.current = false; firmaCliRef.current = null; setFirmaCliOk(false)
  }

  // ── Firma tecnico handlers (WiFi only) ──
  const startFirmaTec = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault(); const canvas = firmaTecCanvasRef.current; if (!canvas) return
    firmaTecDrawing.current = true
    const ctx = canvas.getContext('2d')!
    const pos = getCanvasPos(e, canvas); ctx.beginPath(); ctx.moveTo(pos.x, pos.y)
  }
  const disegnaFirmaTec = (e: React.MouseEvent | React.TouchEvent) => {
    if (!firmaTecDrawing.current) return; e.preventDefault()
    const canvas = firmaTecCanvasRef.current; if (!canvas) return
    const ctx = canvas.getContext('2d')!
    const pos = getCanvasPos(e, canvas); ctx.lineTo(pos.x, pos.y); ctx.stroke()
    firmaTecHasStrokes.current = true
  }
  const stopFirmaTec = (e: React.MouseEvent | React.TouchEvent) => {
    if (!firmaTecDrawing.current) return; e.preventDefault(); firmaTecDrawing.current = false
    const canvas = firmaTecCanvasRef.current; if (!canvas || !firmaTecHasStrokes.current) return
    canvas.toBlob(b => { if (!b || b.size === 0) return; firmaTecRef.current = b; setFirmaTecOk(true) }, 'image/png')
  }
  const cancellaFirmaTec = () => {
    const canvas = firmaTecCanvasRef.current; if (!canvas) return
    const ctx = canvas.getContext('2d')!
    ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, canvas.width, canvas.height)
    firmaTecHasStrokes.current = false; firmaTecRef.current = null; setFirmaTecOk(false)
  }

  // Checklist (non-WiFi only)
  const { data: checklistTemplate } = useQuery({
    queryKey: ['checklist-template', wo.tipologia],
    queryFn: () => checklistApi.list({ tipologia: wo.tipologia }).then(r => r.data.length > 0 ? r.data[0] : null).catch(() => null),
    enabled: !!wo.tipologia,
  })
  const [checklistRisposte, setChecklistRisposte] = useState<Array<{
    voce_id: string; testo: string; tipo: string; completata: boolean | null
    risposta: string; risposta_array: string[]; obbligatoria: boolean
  }>>([])
  useEffect(() => {
    if (checklistTemplate?.voci?.length) {
      setChecklistRisposte(checklistTemplate.voci.map((v: any) => ({
        voce_id: v.id,
        testo: v.testo,
        tipo: v.tipo || 'checkbox',
        completata: null,
        risposta: '',
        risposta_array: [],
        obbligatoria: !!v.obbligatoria,
      })))
    }
  }, [checklistTemplate])

  const _checklistVoceOk = (v: any, r: any): boolean => {
    const tipo = v.tipo || 'checkbox'
    if (tipo === 'testo') return !!r?.risposta?.trim()
    if (tipo === 'select') {
      return v.scelte_multiple
        ? (r?.risposta_array?.length ?? 0) > 0
        : !!r?.risposta?.trim()
    }
    return r?.completata === true || r?.completata === false
  }
  const checklistObbligatorieOk = !checklistTemplate?.voci?.some((v: any) => {
    if (!v.obbligatoria) return false
    const r = checklistRisposte.find(x => x.voce_id === v.id)
    return !_checklistVoceOk(v, r)
  })

  const handleFoto = async (idx: number, file: File | null) => {
    // Ricomprime lo scatto (spesso diversi MB a piena risoluzione) prima di
    // tenerlo in stato: riduce drasticamente il payload dell'invio successivo,
    // soprattutto rilevante su GSM debole in campo.
    const compressed = file ? await compressImage(file) : null
    setFoto(prev => { const n = [...prev]; n[idx] = compressed; return n })
    setFotoUrls(prev => {
      const n = [...prev]
      if (n[idx]) URL.revokeObjectURL(n[idx]!)
      n[idx] = compressed ? URL.createObjectURL(compressed) : null
      return n
    })
  }

  const foto4Ok = foto.every(f => f !== null)

  // Aggiunge/rimuove articolo
  const toggleArticolo = (art: any) => {
    setMaterialiSel(prev => {
      const ex = prev.find(m => m.codice === art.codice)
      if (ex) return prev.filter(m => m.codice !== art.codice)
      return [...prev, { codice: art.codice, descrizione: art.descrizione, quantita: 0, unita_misura: art.unita_misura }]
    })
  }
  const updateQty = (codice: string, q: number) => {
    setMaterialiSel(prev => prev.map(m => m.codice === codice ? { ...m, quantita: q } : m))
  }

  const toggleApparato = (ap: any) => {
    setApparatiSel(prev => {
      const ex = prev.find(a => a.id === ap.id)
      if (ex) return prev.filter(a => a.id !== ap.id)
      return [...prev, { id: ap.id, seriale: ap.seriale, modello: ap.modello, marca: ap.marca, azione: 'installato' }]
    })
  }

  // Elementi aggiunti da ciascun kit: servono per togliere SOLO quelli quando il kit
  // viene deselezionato o sostituito, senza toccare gli apparati scelti a mano.
  const kitElementiRef = useRef<Record<number, { apparati: number[]; materiali: string[] }>>({})
  const rimuoviElementiKit = (kitId: number) => {
    const el = kitElementiRef.current[kitId]
    if (!el) return
    setApparatiSel(prev => prev.filter(a => !el.apparati.includes(a.id)))
    setMaterialiSel(prev => prev.filter(m => !el.materiali.includes(m.codice)))
    delete kitElementiRef.current[kitId]
  }

  // Seleziona/deseleziona un kit (uno alla volta): espande i suoi apparati e articoli nella selezione
  const selezionaKit = async (kit: any) => {
    if (kitLoadingRef.current) return
    if (kitSelezionatiIds.includes(kit.id)) {
      rimuoviElementiKit(kit.id)
      setKitSelezionatiIds([])
      return
    }
    // Un solo kit per intervento: il kit selezionato prima viene sostituito
    kitSelezionatiIds.forEach(rimuoviElementiKit)
    kitLoadingRef.current = true
    try {
      const res = await kitApi.get(kit.id)
      const detail = res.data
      const nuoviApparati: typeof apparatiSel = []
      const nuoviMateriali: typeof materialiSel = []
      for (const m of detail.materiali ?? []) {
        if (m.tipo === 'apparato' && m.apparato_id) {
          nuoviApparati.push({ id: m.apparato_id, seriale: m.seriale, modello: m.nome || '', marca: m.marca || '', azione: 'installato' })
        } else if (m.tipo === 'articolo' && m.articolo_id) {
          nuoviMateriali.push({ codice: m.codice, descrizione: m.nome || '', quantita: m.quantita || 0, unita_misura: m.unita_misura || '' })
        }
      }
      const nuoviUniq = nuoviApparati.filter((na, idx, arr) => arr.findIndex(x => x.id === na.id) === idx)
      const apparatiGiaPresenti = new Set(apparatiSel.map(a => a.id))
      const materialiGiaPresenti = new Set(materialiSel.map(m => m.codice))
      const apparatiAggiunti = nuoviUniq.filter(na => !apparatiGiaPresenti.has(na.id))
      const materialiAggiunti = nuoviMateriali.filter(nm => !materialiGiaPresenti.has(nm.codice))
      kitElementiRef.current[kit.id] = {
        apparati: apparatiAggiunti.map(a => a.id),
        materiali: materialiAggiunti.map(m => m.codice),
      }
      setApparatiSel(prev => [...prev, ...apparatiAggiunti.filter(na => !prev.find(a => a.id === na.id))])
      setMaterialiSel(prev => [...prev, ...materialiAggiunti.filter(nm => !prev.find(x => x.codice === nm.codice))])
      setKitSelezionatiIds([kit.id])
    } catch {
      toast.error('Errore caricamento kit')
    } finally {
      kitLoadingRef.current = false
    }
  }

  const loadPendingRapportini = async (): Promise<RapportiniOffline[]> => {
    try {
      const pending = await getPendingRapportini()
      setPendingRapportini(pending)
      return pending
    } catch (err) {
      console.error('Errore caricamento rapportini offline:', err)
      return []
    }
  }

  useEffect(() => {
    const handleOnline = async () => {
      if (isSyncing.current) return
      isSyncing.current = true
      setIsOnline(true)
      const pending = await loadPendingRapportini()
      if (pending.length > 0) await retryAllRapportini(pending)
      isSyncing.current = false
    }
    const handleOffline = () => setIsOnline(false)

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    loadPendingRapportini()

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  const sendRapportiniOnline = async (
    payload: Record<string, string | undefined>,
    fotoFiles: Array<File | null>
  ) => {
    const fd = new FormData()
    fd.append('wo_id', String(wo.id))
    fd.append('descrizione_lavori', payload.descrizione_lavori ?? '')
    fd.append('note', payload.note ?? '')
    fd.append('nome_cliente', payload.nome_cliente ?? '')
    fd.append('materiali_json', payload.materiali_json ?? '[]')
    fd.append('checklist_json', payload.checklist_json ?? '[]')
    fd.append('firma_tecnico', payload.firma_tecnico ?? '')
    fd.append('apparati_json', payload.apparati_json ?? '[]')
    fd.append('kit_ids_json', payload.kit_ids_json ?? '[]')
    if (payload.num_ap) fd.append('num_ap', payload.num_ap)
    Object.entries(payload).forEach(([k, v]) => {
      if (k.match(/^ap\d+_loc$/) && v) fd.append(k, v)
    })
    fotoFiles.forEach((f, i) => { if (f) fd.append(`foto_${i + 1}`, f) })
    if (firmaCliRef.current) fd.append('firma_cliente', firmaCliRef.current, 'firma_cliente.png')
    if (firmaTecRef.current) fd.append('firma_tecnico_file', firmaTecRef.current, 'firma_tecnico.png')
    if (template) buildFormData(fd)
    return rapportiniApi.inviaCompleto(fd, setSendProgress)
  }

  const retrySingleRapportino = async (rapportini: RapportiniOffline) => {
    try {
      const fd = new FormData()
      fd.append('wo_id', String(rapportini.wo_id))
      fd.append('descrizione_lavori', rapportini.payload.descrizione_lavori ?? '')
      fd.append('note', rapportini.payload.note ?? '')
      fd.append('nome_cliente', rapportini.payload.nome_cliente ?? '')
      fd.append('materiali_json', rapportini.payload.materiali_json ?? '[]')
      fd.append('checklist_json', rapportini.payload.checklist_json ?? '[]')
      fd.append('firma_tecnico', rapportini.payload.firma_tecnico ?? '')
      fd.append('kit_ids_json', rapportini.payload.kit_ids_json ?? '[]')
      const _appRaw = rapportini.payload.apparati_json ?? '[]'
      const _appParsed: any[] = JSON.parse(_appRaw)
      const _appUniq = _appParsed.filter((a, i, arr) => arr.findIndex(x => x.id === a.id) === i)
      fd.append('apparati_json', JSON.stringify(_appUniq))
      if (rapportini.payload.num_ap) fd.append('num_ap', rapportini.payload.num_ap)
      Object.entries(rapportini.payload).forEach(([k, v]) => {
        if (k.match(/^ap\d+_loc$/) && v) fd.append(k, v)
      })
      Object.entries(rapportini.foto).forEach(([key, ab]) => {
        if (ab) {
          const blob = arrayBufferToBlob(ab, 'image/jpeg')
          if (blob) fd.append(key, blob)
        }
      })
      if (rapportini.firma_cliente) {
        const blob = arrayBufferToBlob(rapportini.firma_cliente, 'image/png')
        if (blob) fd.append('firma_cliente', blob, 'firma_cliente.png')
      }
      if (rapportini.firma_tecnico_file) {
        const blob = arrayBufferToBlob(rapportini.firma_tecnico_file, 'image/png')
        if (blob) fd.append('firma_tecnico_file', blob, 'firma_tecnico.png')
      }
      await rapportiniApi.inviaCompleto(fd, setSyncProgress)
      await deleteRapportiniOffline(rapportini.id)
      await loadPendingRapportini()
      toast.success(`Rapportino WO ${rapportini.wo_id} sincronizzato`)
    } catch (err) {
      console.error('Errore retry rapportino:', err)
      toast.error(`Errore invio rapportino WO ${rapportini.wo_id}`)
    } finally {
      setSyncProgress(null)
    }
  }

  const retryAllRapportini = async (items?: RapportiniOffline[]) => {
    const toSend = items ?? pendingRapportini
    if (toSend.length === 0) return
    setSyncingRapportini(true)
    for (const rapportini of toSend) {
      await retrySingleRapportino(rapportini)
    }
    setSyncingRapportini(false)
  }

  const handleInvia = async () => {
    if (!template) {
      if (!nomeCliente.trim()) { toast.error('Nome cliente / responsabile obbligatorio'); return }
      if (!descrizione.trim()) { toast.error('Descrizione lavori obbligatoria'); return }
    }
    if (template) {
      const { valido, campiMancanti } = validaObbligatori()
      if (!valido) { toast.error(`Campi obbligatori mancanti: ${campiMancanti.join(', ')}`); return }
    }
    if (!foto4Ok) { toast.error('Carica tutte e 7 le foto obbligatorie'); return }
    if (!firmaCliOk) { toast.error('Firma del cliente obbligatoria'); return }

    setSending(true)
    setSendProgress(0)
    try {
      const payload: RapportiniOffline['payload'] = {
        descrizione_lavori: descrizione,
        note,
        nome_cliente: nomeCliente,
        materiali_json: JSON.stringify(materialiSel.filter(m => m.quantita >= 1)),
        checklist_json: JSON.stringify(checklistRisposte),
        firma_tecnico: `${user?.nome ?? ''} ${user?.cognome ?? ''}`.trim(),
        apparati_json: JSON.stringify(apparatiSel.filter((a, i, arr) => arr.findIndex(x => x.id === a.id) === i)),
        kit_ids_json: JSON.stringify(kitSelezionatiIds),
      }

      if (isOnline && navigator.onLine) {
        await sendRapportiniOnline(payload, foto)
        toast.success('Rapportino inviato! PDF generato.')
        onSuccess()
      } else {
        // Offline: serializza foto e firme in ArrayBuffer per IndexedDB
        const fotoArrayBuffers: RapportiniOffline['foto'] = {}
        for (let i = 0; i < foto.length; i++) {
          fotoArrayBuffers[`foto_${i + 1}`] = await blobToArrayBuffer(foto[i])
        }
        const rapportiniOfflineData: RapportiniOffline = {
          id: `${wo.id}_${Date.now()}`,
          wo_id: wo.id,
          timestamp: Date.now(),
          payload,
          foto: fotoArrayBuffers,
          firma_cliente: await blobToArrayBuffer(firmaCliRef.current),
          firma_tecnico_file: await blobToArrayBuffer(firmaTecRef.current),
        }
        await saveRapportiniOffline(rapportiniOfflineData)
        await loadPendingRapportini()
        toast.success('Rapportino salvato offline. Verrà inviato al ritorno della connessione.')
        onClose()
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Errore invio rapportino')
    } finally {
      setSending(false)
      setSendProgress(null)
    }
  }

  const STEPS = ['Checklist', 'Materiali', 'Dettagli', 'Foto', 'Firme & Invia']

  const goNext = () => {
    if (step === 1) {
      const voci = checklistTemplate?.voci ?? []
      if (voci.length > 0) {
        const mancanti = voci.filter((v: any) => {
          if (!v.obbligatoria) return false
          const r = checklistRisposte.find(x => x.voce_id === v.id)
          return !_checklistVoceOk(v, r)
        })
        if (mancanti.length > 0) { toast.error(`Completa: ${mancanti[0].testo}`); return }
      }
    }
    if (step === 2) {
      const senzaQty = materialiSel.filter(m => m.quantita < 1)
      if (senzaQty.length > 0) { toast.error(`Inserisci la quantità per: ${senzaQty[0].descrizione}`); return }
    }
    if (step === 3 && !template) {
      if (!nomeCliente.trim()) { toast.error('Inserisci il nome del cliente'); return }
      if (!descrizione.trim()) { toast.error('Inserisci la descrizione lavori'); return }
    }
    if (step === 3 && template) {
      const { valido, campiMancanti } = validaObbligatori()
      if (!valido) { toast.error(`Campi obbligatori mancanti: ${campiMancanti.join(', ')}`); return }
    }
    if (step === 4 && !foto4Ok) { toast.error('Carica tutte e 7 le foto obbligatorie'); return }
    setStep(s => s + 1)
  }

  const goBack = () => {
    setStep(s => s - 1)
  }

  // Helper: blocco canvas firma inline
  const firmaCanvasBlock = (
    label: string,
    ref: React.RefObject<HTMLCanvasElement>,
    ok: boolean,
    onStart: (e: React.MouseEvent | React.TouchEvent) => void,
    onDraw: (e: React.MouseEvent | React.TouchEvent) => void,
    onStop: (e: React.MouseEvent | React.TouchEvent) => void,
    onCancel: () => void,
  ) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <p style={{ fontWeight: 600, color: '#374151', fontSize: 14, margin: 0 }}>{label}</p>
      <canvas ref={ref}
        style={{ width: '100%', maxWidth: FIRMA_W, height: FIRMA_H, border: `2px dashed ${ok ? '#10b981' : '#2563eb'}`, borderRadius: 12, background: '#f8fafc', touchAction: 'none', display: 'block', cursor: 'crosshair' }}
        onMouseDown={onStart} onMouseMove={onDraw} onMouseUp={onStop} onMouseLeave={onStop}
        onTouchStart={onStart} onTouchMove={onDraw} onTouchEnd={onStop}
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <button type="button" onClick={onCancel}
          style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #e5e7eb', background: '#f9fafb', fontSize: 12, cursor: 'pointer' }}>
          🗑 Cancella
        </button>
        {ok
          ? <span style={{ fontSize: 12, color: '#10b981', fontWeight: 600 }}>✓ Firmato</span>
          : <span style={{ fontSize: 12, color: '#94a3b8' }}>Firma qui sopra</span>}
      </div>
    </div>
  )

  return (
    <>
    {pendingRapportini.length > 0 && isOnline && (
      <div style={{ position: 'fixed', bottom: 16, right: 16, background: '#1E40AF', color: '#fff', padding: '12px 16px', borderRadius: 10, boxShadow: '0 4px 20px rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', gap: 12, zIndex: 9999 }}>
        <div>
          <p style={{ fontWeight: 600, margin: 0, fontSize: 14 }}>{pendingRapportini.length} rapportini in sospeso</p>
          <p style={{ margin: 0, fontSize: 12, opacity: 0.85 }}>Connessione ripristinata</p>
        </div>
        <button
          onClick={() => retryAllRapportini()}
          disabled={syncingRapportini}
          style={{ background: '#fff', color: '#1E40AF', border: 'none', borderRadius: 6, padding: '6px 12px', fontWeight: 600, fontSize: 13, cursor: syncingRapportini ? 'not-allowed' : 'pointer', opacity: syncingRapportini ? 0.6 : 1 }}
        >
          {syncingRapportini
            ? `Sincronizzazione... ${syncProgress != null ? `${syncProgress}%` : ''}`
            : 'Sincronizza'}
        </button>
      </div>
    )}
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 50, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '12px', WebkitOverflowScrolling: 'touch' as any }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div style={{ background: '#fff', borderRadius: 20, width: '100%', maxWidth: 480, display: 'flex', flexDirection: 'column', margin: 'auto', minHeight: 'min-content', maxHeight: '90vh', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        {/* Header */}
        <div style={{ padding: '20px 20px 16px', borderBottom: '1px solid #f1f5f9', flexShrink: 0 }}>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-slate-900">
              {'Segna come Eseguito'}
            </h2>
            <button onClick={onClose} className="btn-ghost px-2">✕</button>
          </div>
          <div className="flex gap-1">
            {STEPS.map((s, i) => (
              <div key={s} className="flex items-center gap-1 flex-1">
                <div className={`flex-1 h-1.5 rounded-full transition-all ${i + 1 <= step ? 'bg-green-500' : 'bg-slate-200'}`} />
                <span style={{ fontSize: 9, whiteSpace: 'nowrap', color: i + 1 === step ? '#10B981' : '#94A3B8', fontWeight: i + 1 === step ? 700 : 400 }}>
                  {i + 1}. {s}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div style={{ overflowY: 'auto', flex: 1, padding: '16px 20px', WebkitOverflowScrolling: 'touch' as any, minHeight: 0 }}>

          {false && step === 1 && (
            <div className="space-y-4">
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', border: `2px solid ${isSopralluogo ? '#3b82f6' : '#e2e8f0'}`, borderRadius: 10, cursor: 'pointer', background: isSopralluogo ? '#eff6ff' : '#fff' }}>
                <input type="checkbox" checked={isSopralluogo} onChange={e => { setIsSopralluogo(e.target.checked); if (e.target.checked) setNumAp(0) }}
                  style={{ width: 18, height: 18, accentColor: '#3b82f6' }} />
                <div>
                  <p style={{ fontWeight: 600, fontSize: 14, margin: 0, color: isSopralluogo ? '#1d4ed8' : '#374151' }}>Solo sopralluogo</p>
                  <p style={{ fontSize: 12, color: '#64748b', margin: 0 }}>Nessun AP installato — foto e firma opzionali</p>
                </div>
              </label>
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm text-slate-600">{magData?.targa ? `Materiali dal furgone ${magData.targa}` : 'Magazzino centrale'}</p>
              </div>
              {(kitSulFurgone.length > 0 || magData?.apparati?.length > 0) && (
                <div className="relative">
                  <input
                    type="text"
                    className="input text-sm pr-8 w-full"
                    placeholder="Cerca per seriale o numero kit..."
                    value={ricercaFurgone}
                    onChange={e => setRicercaFurgone(e.target.value)}
                  />
                  {ricercaFurgone && (
                    <button onClick={() => setRicercaFurgone('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-lg leading-none">×</button>
                  )}
                </div>
              )}
              {kitFiltrati.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-1.5"><Archive size={12} /> Kit disponibili</h3>
                  <div className="space-y-2">
                    {kitFiltrati.map((kit: any) => {
                      const selezionato = kitSelezionatiIds.includes(kit.id)
                      return (
                        <div key={kit.id} onClick={() => selezionaKit(kit)} className="rounded-lg border cursor-pointer transition-colors"
                          style={{ borderColor: selezionato ? '#7c3aed' : '#e2e8f0', background: selezionato ? '#f5f3ff' : '#fff' }}>
                          <div className="flex items-center justify-between px-3 py-2">
                            <div className="flex items-center gap-2">
                              <input type="radio" readOnly checked={selezionato} className="accent-purple-600" />
                              <span className="font-mono text-sm font-semibold text-purple-700">{kit.nome}</span>
                              {kit.descrizione && <span className="text-xs text-slate-500">{kit.descrizione}</span>}
                            </div>
                            <span className="text-xs text-slate-400">{kit.materiali?.length ?? 0} elementi</span>
                          </div>
                          {selezionato && kit.materiali?.length > 0 && (
                            <div className="px-3 pb-2 space-y-0.5">
                              {kit.materiali.map((m: any, mi: number) => (
                                <div key={mi} className="flex items-center gap-2 text-xs text-slate-600">
                                  <span className="text-slate-400">•</span>
                                  {m.seriale && <span className="font-mono text-blue-600">{m.seriale}</span>}
                                  <span>{m.nome}</span>
                                  {<span className={m.quantita < 1 ? 'text-red-500 font-bold' : 'text-slate-400'}>×{m.quantita || '?'}</span>}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
              {apparatiFiltrati.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Apparati disponibili</h3>
                  <div className="space-y-1 max-h-60 overflow-y-auto">
                    {apparatiFiltrati.map((ap: any) => {
                      const sel = apparatiSel.find(a => a.id === ap.id)
                      return (
                        <label key={ap.id} className="flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:bg-slate-50">
                          <input type="checkbox" checked={!!sel} onChange={() => toggleApparato(ap)} />
                          <span className="text-xs font-mono">{ap.seriale}</span>
                          <span className="text-xs text-slate-600">{ap.modello} — {ap.marca}</span>
                        </label>
                      )
                    })}
                  </div>
                </div>
              )}
              {articoliFiltrati.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Materiali di consumo</h3>
                  <div className="space-y-1 max-h-48 overflow-y-auto">
                    {articoliFiltrati.map((art: any) => {
                      const sel = materialiSel.find(m => m.codice === art.codice)
                      return (
                        <div key={art.codice} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50">
                          <input type="checkbox" checked={!!sel} onChange={() => toggleArticolo(art)} />
                          <span className="text-xs font-mono w-24 shrink-0">{art.codice}</span>
                          <span className="text-xs text-slate-600 flex-1">{art.descrizione}</span>
                          <span className="text-xs text-slate-400">disp. {art.quantita_disponibile} {art.unita_misura}</span>
                          {sel && <input type="number" min={1} max={art.quantita_disponibile} value={sel.quantita || ''} placeholder="Qtà" onChange={e => updateQty(art.codice, Number(e.target.value))} className="input w-16 text-xs py-1" />}
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
              {qFurg && kitFiltrati.length === 0 && apparatiFiltrati.length === 0 && articoliFiltrati.length === 0 && (
                <p className="text-sm text-slate-400 text-center py-4">Nessun risultato per "{ricercaFurgone}"</p>
              )}
              {!qFurg && !magData?.apparati?.length && !magData?.articoli?.length && (
                <p className="text-sm text-slate-400 text-center py-8">Nessun materiale disponibile nel magazzino</p>
              )}

              {/* ── Crea manualmente ── */}
              <div style={{ border: '1.5px dashed #c7d2fe', borderRadius: 10, padding: '12px 14px', background: '#f8f9ff' }}>
                <div className="flex items-center justify-between mb-2">
                  <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#4f46e5' }}>Crea manualmente</span>
                  <button type="button" onClick={addManualRow}
                    style={{ fontSize: 12, color: '#4f46e5', background: '#ede9fe', border: 'none', borderRadius: 6, padding: '2px 10px', cursor: 'pointer', fontWeight: 600 }}>
                    + Aggiungi
                  </button>
                </div>
                <div className="space-y-2">
                  {manualRows.map((row, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input
                        type="text"
                        value={row.modello}
                        onChange={e => updateManualRow(i, 'modello', e.target.value)}
                        placeholder="Modello"
                        style={{ width: 90, fontSize: 12, padding: '6px 8px', borderRadius: 6, border: '1px solid #d1d5db', fontFamily: 'monospace' }}
                      />
                      <input
                        type="text"
                        value={row.seriale}
                        onChange={e => updateManualRow(i, 'seriale', e.target.value.toUpperCase())}
                        placeholder="Seriale apparato"
                        style={{ flex: 1, fontSize: 12, padding: '6px 8px', borderRadius: 6, border: '1px solid #d1d5db', fontFamily: 'monospace' }}
                      />
                      {manualRows.length > 1 && (
                        <button type="button" onClick={() => removeManualRow(i)}
                          style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>×</button>
                      )}
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={salvaApparatiManuali}
                  disabled={salvaManualePending || !manualRows.some(r => r.seriale.trim())}
                  style={{ marginTop: 10, width: '100%', padding: '8px 0', borderRadius: 8, background: '#4f46e5', color: '#fff', fontWeight: 700, fontSize: 13, border: 'none', cursor: 'pointer', opacity: salvaManualePending || !manualRows.some(r => r.seriale.trim()) ? 0.5 : 1 }}
                >
                  {salvaManualePending ? 'Salvataggio...' : '💾 Salva in magazzino e aggiungi'}
                </button>
              </div>
            </div>
          )}

          {/* ── WIFI STEP 2 — AP WiFi ─── */}
          {false && step === 2 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <p style={{ fontWeight: 700, color: '#1E40AF', fontSize: 15, margin: 0 }}>Dati Access Point installati</p>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>Numero AP installati</label>
                <select value={numAp} onChange={e => setNumAp(parseInt(e.target.value))}
                  style={{ display: 'block', width: '100%', marginTop: 6, padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14 }}>
                  <option value={0}>Nessuno (0 AP)</option>
                  <option value={1}>1 AP</option>
                  <option value={2}>2 AP</option>
                  <option value={3}>3 AP</option>
                  <option value={4}>4 AP</option>
                </select>
              </div>
              {Array.from({ length: numAp }, (_, i) => (
                <div key={i} style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: 12 }}>
                  <p style={{ fontWeight: 700, color: '#1E40AF', fontSize: 13, margin: '0 0 10px' }}>AP {i + 1}</p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div>
                      <label style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>Locazione</label>
                      <input type="text" placeholder={`es. Aula 12, piano ${i + 1}`} value={apLocazioni[i]}
                        onChange={e => { const n = [...apLocazioni]; n[i] = e.target.value; setApLocazioni(n) }}
                        style={{ display: 'block', width: '100%', marginTop: 4, padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13, boxSizing: 'border-box' }} />
                    </div>
                    <div>
                      <label style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>Seriale AP</label>
                      <input type="text" placeholder="es. FC:EC:DA:12:34:56" value={apSeriali[i]}
                        onChange={e => { const n = [...apSeriali]; n[i] = e.target.value; setApSeriali(n) }}
                        style={{ display: 'block', width: '100%', marginTop: 4, padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13, fontFamily: 'monospace', boxSizing: 'border-box' }} />
                    </div>
                  </div>
                </div>
              ))}
              {numAp === 0 && <p style={{ textAlign: 'center', color: '#94a3b8', fontSize: 13, padding: '16px 0' }}>Seleziona il numero di AP per inserire i dati</p>}
            </div>
          )}

          {/* ── WIFI STEP 3 — FOTO ─── */}
          {false && step === 3 && (
            <div className="space-y-3">
              {isSopralluogo && (
                <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, padding: '8px 12px', fontSize: 13, color: '#0369a1' }}>
                  ℹ Solo sopralluogo: le foto sono opzionali ma consigliate.
                </div>
              )}
              {!isSopralluogo && <p className="text-sm text-slate-600">Sono obbligatorie <strong>tutte e 7 le foto</strong>.</p>}
              <div className="grid grid-cols-2 gap-3">
                {FOTO_LABELS.map((lbl, i) => (
                  <div key={i}>
                    <label className="label" style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.3 }}>{lbl}{!isSopralluogo ? ' *' : ''}</label>
                    <div className={`rounded-xl border-2 border-dashed overflow-hidden transition-all ${foto[i] ? 'border-green-400 bg-green-50' : 'border-slate-300 bg-slate-50'}`}>
                      {foto[i] ? (
                        <>
                          <img src={fotoUrls[i]!} className="object-cover w-full" style={{ height: 160 }} />
                          <div className="flex border-t border-slate-200">
                            <label className="flex-1 flex items-center justify-center gap-1 py-1.5 text-xs text-slate-600 cursor-pointer hover:bg-slate-100 active:bg-slate-200">
                              📷 Camera
                              <input type="file" accept="image/*" capture="environment" className="hidden" onChange={e => handleFoto(i, e.target.files?.[0] || null)} />
                            </label>
                            <div className="w-px bg-slate-200" />
                            <label className="flex-1 flex items-center justify-center gap-1 py-1.5 text-xs text-slate-600 cursor-pointer hover:bg-slate-100 active:bg-slate-200">
                              🖼 Rullino
                              <input type="file" accept="image/*" className="hidden" onChange={e => handleFoto(i, e.target.files?.[0] || null)} />
                            </label>
                          </div>
                        </>
                      ) : (
                        <div className="flex" style={{ height: 160 }}>
                          <label className="flex-1 flex flex-col items-center justify-center gap-1 cursor-pointer hover:bg-blue-50 active:bg-blue-100 transition-colors">
                            <span style={{ fontSize: 28 }}>📷</span>
                            <span className="text-xs text-slate-500 font-medium">Fotocamera</span>
                            <input type="file" accept="image/*" capture="environment" className="hidden" onChange={e => handleFoto(i, e.target.files?.[0] || null)} />
                          </label>
                          <div className="w-px bg-slate-200 my-3" />
                          <label className="flex-1 flex flex-col items-center justify-center gap-1 cursor-pointer hover:bg-blue-50 active:bg-blue-100 transition-colors">
                            <span style={{ fontSize: 28 }}>🖼️</span>
                            <span className="text-xs text-slate-500 font-medium">Rullino</span>
                            <input type="file" accept="image/*" className="hidden" onChange={e => handleFoto(i, e.target.files?.[0] || null)} />
                          </label>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {foto4Ok && <div className="bg-green-50 border border-green-200 rounded-lg p-2 text-xs text-green-700 text-center">✓ Tutte e 7 le foto caricate</div>}
            </div>
          )}

          {/* ── WIFI STEP 4 — FIRME ─── */}
          {false && step === 4 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              <div style={{ background: '#f0fdf4', borderRadius: 8, padding: '8px 12px', fontSize: 13, color: '#15803d' }}>
                ✓ Tecnico: <strong>{user?.nome} {user?.cognome}</strong>
              </div>
              {firmaCanvasBlock('Firma Cliente / Responsabile Scuola', firmaClienteRef, firmaCliOk, startFirma, disegnaFirma, stopFirma, cancellaFirma)}
              {firmaCanvasBlock('Firma Tecnico', firmaTecCanvasRef, firmaTecOk, startFirmaTec, disegnaFirmaTec, stopFirmaTec, cancellaFirmaTec)}
            </div>
          )}

          {/* ── WIFI STEP 5 — RIEPILOGO & INVIA ─── */}
          {false && step === 5 && (
            <div className="space-y-4">
              <div>
                <label className="label">Nome Cliente / Responsabile sito *</label>
                <input className="input" autoComplete="new-password" name={`nome-cliente-${wo.id}`}
                  value={nomeCliente} onChange={e => setNomeCliente(e.target.value)} placeholder="Nome e cognome del cliente presente" />
              </div>
              <div>
                <label className="label">Descrizione lavori svolti *</label>
                <textarea className="input" rows={4} value={descrizione} onChange={e => setDescrizione(e.target.value)}
                  placeholder="Descrivere dettagliatamente i lavori eseguiti, AP installati, configurazioni..." />
              </div>
              <div>
                <label className="label">Note aggiuntive</label>
                <textarea className="input" rows={2} value={note} onChange={e => setNote(e.target.value)}
                  placeholder="Anomalie, lavori futuri, accessi speciali..." />
              </div>
              <div style={{ background: '#f8fafc', borderRadius: 10, padding: '12px 14px', fontSize: 13 }}>
                <p style={{ fontWeight: 700, color: '#374151', marginBottom: 6 }}>Riepilogo</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  <span style={{ background: '#eff6ff', color: '#1E40AF', borderRadius: 99, padding: '3px 8px', fontSize: 12, fontWeight: 600 }}>WO {wo?.numero_progressivo}</span>
                  {isSopralluogo && <span style={{ background: '#fef3c7', color: '#92400e', borderRadius: 99, padding: '3px 8px', fontSize: 12 }}>Solo sopralluogo</span>}
                  {!isSopralluogo && <span style={{ background: '#f0fdf4', color: '#15803d', borderRadius: 99, padding: '3px 8px', fontSize: 12 }}>{numAp} AP</span>}
                  <span style={{ background: foto4Ok ? '#f0fdf4' : '#fef9c3', color: foto4Ok ? '#15803d' : '#92400e', borderRadius: 99, padding: '3px 8px', fontSize: 12 }}>{foto.filter(Boolean).length}/7 foto</span>
                  <span style={{ background: firmaCliOk ? '#f0fdf4' : (isSopralluogo ? '#f8fafc' : '#fef9c3'), color: firmaCliOk ? '#15803d' : (isSopralluogo ? '#64748b' : '#92400e'), borderRadius: 99, padding: '3px 8px', fontSize: 12 }}>
                    {firmaCliOk ? '✓ Firma cli.' : (isSopralluogo ? '— firma cli.' : '○ Firma cli.')}
                  </span>
                  <span style={{ background: firmaTecOk ? '#f0fdf4' : '#f8fafc', color: firmaTecOk ? '#15803d' : '#64748b', borderRadius: 99, padding: '3px 8px', fontSize: 12 }}>
                    {firmaTecOk ? '✓ Firma tec.' : '— firma tec.'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* ── STEP 1 — CHECKLIST ─── */}
          {step === 1 && (
            <div className="space-y-3">
              {!checklistTemplate?.voci?.length ? (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                  <CheckSquare size={32} style={{ margin: '0 auto 12px', color: '#94a3b8' }} />
                  <p className="text-sm text-slate-500">Nessuna checklist configurata per questa tipologia.</p>
                  <p className="text-xs text-slate-400 mt-1">Puoi procedere direttamente.</p>
                </div>
              ) : (
                <>
                  <p className="text-sm text-slate-600">
                    <strong>{checklistTemplate.nome}</strong> — spunta ogni voce completata.
                    Le voci con <span style={{ color: '#ef4444' }}>*</span> sono obbligatorie.
                  </p>
                  <div className="space-y-2">
                    {checklistTemplate.voci.map((v: any) => {
                      const r = checklistRisposte.find(x => x.voce_id === v.id)
                      const tipo = v.tipo || 'checkbox'
                      const isCompleted = _checklistVoceOk(v, r)
                      const setR = (changes: object) =>
                        setChecklistRisposte(prev => prev.map(x => x.voce_id === v.id ? { ...x, ...changes } : x))
                      return (
                        <div key={v.id} className={`p-3 rounded-lg border transition-all ${isCompleted ? 'border-green-300 bg-green-50' : 'border-slate-200 bg-white'}`}>
                          <div className="flex items-start gap-2 mb-1">
                            <span className="text-sm flex-1">
                              {v.testo}{v.obbligatoria && <span style={{ color: '#ef4444', marginLeft: 4 }}>*</span>}
                            </span>
                            {isCompleted && <span style={{ color: '#10b981', fontSize: 13 }}>✓</span>}
                          </div>
                          {tipo === 'checkbox' && (
                            <div className="campo-yesno mt-1">
                              <button type="button"
                                className={`campo-yesno-btn yes${r?.completata === true ? ' active' : ''}`}
                                onClick={() => setR({ completata: r?.completata === true ? null : true })}>
                                ✓ Sì
                              </button>
                              <button type="button"
                                className={`campo-yesno-btn no${r?.completata === false ? ' active' : ''}`}
                                onClick={() => setR({ completata: r?.completata === false ? null : false })}>
                                ✗ No
                              </button>
                            </div>
                          )}
                          {tipo === 'testo' && (
                            <input type="text" className="input mt-1 text-sm w-full"
                              placeholder={v.obbligatoria ? 'Campo obbligatorio — inserisci risposta...' : 'Risposta (opzionale)'}
                              value={r?.risposta || ''}
                              onChange={e => setR({ risposta: e.target.value })} />
                          )}
                          {tipo === 'select' && !v.scelte_multiple && (
                            <div className="flex flex-wrap gap-2 mt-1">
                              {(v.opzioni ?? []).map((o: string) => (
                                <button key={o} type="button"
                                  className={`px-3 py-1.5 rounded-full text-xs border font-medium transition-all ${r?.risposta === o ? 'bg-blue-600 text-white border-blue-600' : 'bg-white border-slate-300 text-slate-700 hover:border-blue-400'}`}
                                  onClick={() => setR({ risposta: r?.risposta === o ? '' : o })}>
                                  {o}
                                </button>
                              ))}
                            </div>
                          )}
                          {tipo === 'select' && v.scelte_multiple && (
                            <div className="campo-checkbox-group mt-1">
                              {(v.opzioni ?? []).map((o: string) => {
                                const sel: string[] = r?.risposta_array ?? []
                                const checked = sel.includes(o)
                                return (
                                  <label key={o} className="campo-checkbox-item">
                                    <input type="checkbox" checked={checked}
                                      onChange={e => setR({
                                        risposta_array: v.scelte_esclusive
                                          ? (checked ? [] : [o])
                                          : (e.target.checked ? [...sel, o] : sel.filter(x => x !== o))
                                      })} />
                                    {o}
                                  </label>
                                )
                              })}
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                  {checklistObbligatorieOk
                    ? <div className="bg-green-50 border border-green-200 rounded-lg p-2 text-xs text-green-700 text-center">✓ Tutte le voci obbligatorie completate</div>
                    : <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 text-xs text-amber-700 text-center">Completa le voci obbligatorie (*) per procedere</div>
                  }
                </>
              )}
            </div>
          )}

          {/* ── NON-WIFI STEP 2 — MATERIALI ─── */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm text-slate-600">{magData?.targa ? `Materiali dal furgone ${magData.targa}` : 'Magazzino centrale'}</p>
              </div>
              {(kitSulFurgone.length > 0 || magData?.apparati?.length > 0) && (
                <div className="relative">
                  <input
                    type="text"
                    className="input text-sm pr-8 w-full"
                    placeholder="Cerca per seriale o numero kit..."
                    value={ricercaFurgone}
                    onChange={e => setRicercaFurgone(e.target.value)}
                  />
                  {ricercaFurgone && (
                    <button onClick={() => setRicercaFurgone('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-lg leading-none">×</button>
                  )}
                </div>
              )}
              {kitFiltrati.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-1.5"><Archive size={12} /> Kit disponibili</h3>
                  <div className="space-y-2">
                    {kitFiltrati.map((kit: any) => {
                      const selezionato = kitSelezionatiIds.includes(kit.id)
                      return (
                        <div key={kit.id} onClick={() => selezionaKit(kit)} className="rounded-lg border cursor-pointer transition-colors"
                          style={{ borderColor: selezionato ? '#7c3aed' : '#e2e8f0', background: selezionato ? '#f5f3ff' : '#fff' }}>
                          <div className="flex items-center justify-between px-3 py-2">
                            <div className="flex items-center gap-2">
                              <input type="radio" readOnly checked={selezionato} className="accent-purple-600" />
                              <span className="font-mono text-sm font-semibold text-purple-700">{kit.nome}</span>
                              {kit.descrizione && <span className="text-xs text-slate-500">{kit.descrizione}</span>}
                            </div>
                            <span className="text-xs text-slate-400">{kit.materiali?.length ?? 0} elementi</span>
                          </div>
                          {selezionato && kit.materiali?.length > 0 && (
                            <div className="px-3 pb-2 space-y-0.5">
                              {kit.materiali.map((m: any, i: number) => (
                                <div key={i} className="flex items-center gap-2 text-xs text-slate-600">
                                  <span className="text-slate-400">•</span>
                                  {m.seriale && <span className="font-mono text-blue-600">{m.seriale}</span>}
                                  <span>{m.nome}</span>
                                  {<span className={m.quantita < 1 ? 'text-red-500 font-bold' : 'text-slate-400'}>×{m.quantita || '?'}</span>}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                  <p className="text-xs text-slate-400 mt-1">Selezionando un kit tutti i suoi elementi vengono aggiunti automaticamente</p>
                </div>
              )}
              {apparatiFiltrati.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Apparati disponibili</h3>
                  <div className="space-y-1 max-h-60 overflow-y-auto">
                    {apparatiFiltrati.map((ap: any) => {
                      const sel = apparatiSel.find(a => a.id === ap.id)
                      return (
                        <label key={ap.id} className="flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:bg-slate-50">
                          <input type="checkbox" checked={!!sel} onChange={() => toggleApparato(ap)} />
                          <span className="text-xs font-mono">{ap.seriale}</span>
                          <span className="text-xs text-slate-600">{ap.modello} — {ap.marca}</span>
                        </label>
                      )
                    })}
                  </div>
                </div>
              )}
              {articoliFiltrati.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Materiali di consumo</h3>
                  <div className="space-y-1 max-h-48 overflow-y-auto">
                    {articoliFiltrati.map((art: any) => {
                      const sel = materialiSel.find(m => m.codice === art.codice)
                      return (
                        <div key={art.codice} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50">
                          <input type="checkbox" checked={!!sel} onChange={() => toggleArticolo(art)} />
                          <span className="text-xs font-mono w-24 shrink-0">{art.codice}</span>
                          <span className="text-xs text-slate-600 flex-1">{art.descrizione}</span>
                          <span className="text-xs text-slate-400">disp. {art.quantita_disponibile} {art.unita_misura}</span>
                          {sel && <input type="number" min={1} max={art.quantita_disponibile} value={sel.quantita || ''} placeholder="Qtà" onChange={e => updateQty(art.codice, Number(e.target.value))} className="input w-16 text-xs py-1" />}
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
              {qFurg && kitFiltrati.length === 0 && apparatiFiltrati.length === 0 && articoliFiltrati.length === 0 && (
                <p className="text-sm text-slate-400 text-center py-4">Nessun risultato per "{ricercaFurgone}"</p>
              )}
              {!qFurg && !magData?.apparati?.length && !magData?.articoli?.length && (
                <p className="text-sm text-slate-400 text-center py-8">Nessun materiale disponibile nel magazzino</p>
              )}

              {/* ── Crea manualmente ── */}
              <div style={{ border: '1.5px dashed #c7d2fe', borderRadius: 10, padding: '12px 14px', background: '#f8f9ff' }}>
                <div className="flex items-center justify-between mb-2">
                  <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#4f46e5' }}>Crea manualmente</span>
                  <button type="button" onClick={addManualRow}
                    style={{ fontSize: 12, color: '#4f46e5', background: '#ede9fe', border: 'none', borderRadius: 6, padding: '2px 10px', cursor: 'pointer', fontWeight: 600 }}>
                    + Aggiungi
                  </button>
                </div>
                <div className="space-y-2">
                  {manualRows.map((row, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input
                        type="text"
                        value={row.modello}
                        onChange={e => updateManualRow(i, 'modello', e.target.value)}
                        placeholder="Modello"
                        style={{ width: 90, fontSize: 12, padding: '6px 8px', borderRadius: 6, border: '1px solid #d1d5db', fontFamily: 'monospace' }}
                      />
                      <input
                        type="text"
                        value={row.seriale}
                        onChange={e => updateManualRow(i, 'seriale', e.target.value.toUpperCase())}
                        placeholder="Seriale apparato"
                        style={{ flex: 1, fontSize: 12, padding: '6px 8px', borderRadius: 6, border: '1px solid #d1d5db', fontFamily: 'monospace' }}
                      />
                      {manualRows.length > 1 && (
                        <button type="button" onClick={() => removeManualRow(i)}
                          style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>×</button>
                      )}
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={salvaApparatiManuali}
                  disabled={salvaManualePending || !manualRows.some(r => r.seriale.trim())}
                  style={{ marginTop: 10, width: '100%', padding: '8px 0', borderRadius: 8, background: '#4f46e5', color: '#fff', fontWeight: 700, fontSize: 13, border: 'none', cursor: 'pointer', opacity: salvaManualePending || !manualRows.some(r => r.seriale.trim()) ? 0.5 : 1 }}
                >
                  {salvaManualePending ? 'Salvataggio...' : '💾 Salva in magazzino e aggiungi'}
                </button>
              </div>

              {(materialiSel.length > 0 || apparatiSel.length > 0) && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-xs text-green-700">
                  Selezionati: {apparatiSel.length} apparati, {materialiSel.length} tipi di materiale
                </div>
              )}
            </div>
          )}

          {/* ── NON-WIFI STEP 3 — DETTAGLI (dinamico o legacy) ─── */}
          {step === 3 && (
            <div className="space-y-4">
              {templateLoading && (
                <div className="template-loading">Caricamento form...</div>
              )}
              {templateError && templateError !== 'NO_TEMPLATE' && (
                <div className="template-error">⚠ {templateError}</div>
              )}
              {(templateError === 'NO_TEMPLATE' || (!template && !templateLoading && !templateError)) && (
                <>
                  <div>
                    <label className="label">Nome Cliente / Responsabile sito *</label>
                    <input className="input" autoComplete="new-password" name={`nome-cliente-${wo.id}`} value={nomeCliente} onChange={e => setNomeCliente(e.target.value)} placeholder="Nome e cognome del cliente presente" />
                  </div>
                  <div>
                    <label className="label">Descrizione lavori svolti *</label>
                    <textarea className="input" rows={5} value={descrizione} onChange={e => setDescrizione(e.target.value)}
                      placeholder="Descrivere dettagliatamente i lavori eseguiti, apparati installati, configurazioni effettuate..." />
                  </div>
                  <div>
                    <label className="label">Note aggiuntive</label>
                    <textarea className="input" rows={3} value={note} onChange={e => setNote(e.target.value)}
                      placeholder="Anomalie riscontrate, lavori futuri necessari, accessi speciali..." />
                  </div>
                </>
              )}
              {template && !templateLoading && (
                <div className="rapportino-form-dinamico">
                  {template.campi.map(campo => (
                    <CampoRapportino
                      key={campo.id}
                      campo={campo}
                      valore={templateValori[campo.id]}
                      onChange={v => setTemplateValore(campo.id, v)}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── NON-WIFI STEP 4 — FOTO ─── */}
          {step === 4 && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">Sono obbligatorie <strong>tutte e 7 le foto</strong>.</p>
              <div className="grid grid-cols-2 gap-3">
                {FOTO_LABELS.map((lbl, i) => (
                  <div key={i}>
                    <label className="label" style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.3 }}>{lbl} *</label>
                    <div className={`rounded-xl border-2 border-dashed overflow-hidden transition-all ${foto[i] ? 'border-green-400 bg-green-50' : 'border-slate-300 bg-slate-50'}`}>
                      {foto[i] ? (
                        <>
                          <img src={fotoUrls[i]!} className="object-cover w-full" style={{ height: 160 }} />
                          <div className="flex border-t border-slate-200">
                            <label className="flex-1 flex items-center justify-center gap-1 py-1.5 text-xs text-slate-600 cursor-pointer hover:bg-slate-100 active:bg-slate-200">
                              📷 Camera
                              <input type="file" accept="image/*" capture="environment" className="hidden" onChange={e => handleFoto(i, e.target.files?.[0] || null)} />
                            </label>
                            <div className="w-px bg-slate-200" />
                            <label className="flex-1 flex items-center justify-center gap-1 py-1.5 text-xs text-slate-600 cursor-pointer hover:bg-slate-100 active:bg-slate-200">
                              🖼 Rullino
                              <input type="file" accept="image/*" className="hidden" onChange={e => handleFoto(i, e.target.files?.[0] || null)} />
                            </label>
                          </div>
                        </>
                      ) : (
                        <div className="flex" style={{ height: 160 }}>
                          <label className="flex-1 flex flex-col items-center justify-center gap-1 cursor-pointer hover:bg-blue-50 active:bg-blue-100 transition-colors">
                            <span style={{ fontSize: 28 }}>📷</span>
                            <span className="text-xs text-slate-500 font-medium">Fotocamera</span>
                            <input type="file" accept="image/*" capture="environment" className="hidden" onChange={e => handleFoto(i, e.target.files?.[0] || null)} />
                          </label>
                          <div className="w-px bg-slate-200 my-3" />
                          <label className="flex-1 flex flex-col items-center justify-center gap-1 cursor-pointer hover:bg-blue-50 active:bg-blue-100 transition-colors">
                            <span style={{ fontSize: 28 }}>🖼️</span>
                            <span className="text-xs text-slate-500 font-medium">Rullino</span>
                            <input type="file" accept="image/*" className="hidden" onChange={e => handleFoto(i, e.target.files?.[0] || null)} />
                          </label>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {foto4Ok && <div className="bg-green-50 border border-green-200 rounded-lg p-2 text-xs text-green-700 text-center">✓ Tutte e 7 le foto caricate</div>}
            </div>
          )}

          {/* ── NON-WIFI STEP 5 — FIRMA & INVIA ─── */}
          {step === 5 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <p style={{ fontWeight: 700, color: '#1E40AF', fontSize: 15, margin: '0 0 2px' }}>Firma Cliente</p>
                <p style={{ fontSize: 13, fontWeight: 600, color: '#111827', margin: 0 }}>{wo?.denominazione_sito || wo?.cliente_nome || ''}</p>
              </div>
              <canvas ref={firmaClienteRef}
                style={{ width: '100%', maxWidth: FIRMA_W, height: FIRMA_H, border: '2px dashed #2563eb', borderRadius: 14, background: '#f8fafc', touchAction: 'none', display: 'block', cursor: 'crosshair' }}
                onMouseDown={startFirma} onMouseMove={disegnaFirma} onMouseUp={stopFirma} onMouseLeave={stopFirma}
                onTouchStart={startFirma} onTouchMove={disegnaFirma} onTouchEnd={stopFirma}
              />
              <button type="button" onClick={cancellaFirma}
                style={{ alignSelf: 'flex-start', padding: '6px 14px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#f9fafb', fontSize: 13, cursor: 'pointer' }}>
                🗑 Cancella firma
              </button>
              <div style={{ background: '#f0fdf4', borderRadius: 10, padding: '10px 14px', fontSize: 13, color: '#15803d' }}>
                ✓ Tecnico: {user?.nome} {user?.cognome}
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ background: '#eff6ff', color: '#1E40AF', borderRadius: 99, padding: '4px 10px', fontSize: 12, fontWeight: 600 }}>WO {wo?.numero_progressivo}</span>
                {foto.filter(Boolean).length > 0 && <span style={{ background: '#f0fdf4', color: '#15803d', borderRadius: 99, padding: '4px 10px', fontSize: 12, fontWeight: 600 }}>{foto.filter(Boolean).length}/7 foto</span>}
                {!firmaCliOk
                  ? <span style={{ background: '#fef9c3', color: '#92400e', borderRadius: 99, padding: '4px 10px', fontSize: 12, fontWeight: 600 }}>○ Firma mancante</span>
                  : <span style={{ background: '#f0fdf4', color: '#15803d', borderRadius: 99, padding: '4px 10px', fontSize: 12, fontWeight: 600 }}>✓ Firmato</span>}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '16px 20px', borderTop: '1px solid #e5e7eb', background: '#fff', flexShrink: 0, display: 'flex', gap: 12 }}>
          {step > 1 && <button onClick={goBack} className="btn-secondary">← Indietro</button>}
          <div className="flex-1" />
          {step < maxStep && <button onClick={goNext} className="btn-primary">Avanti →</button>}
          {step === maxStep && (
            <button
              onClick={handleInvia}
              disabled={sending}
              className="btn-primary flex items-center gap-2"
              style={{ background: sending ? '#94A3B8' : '#10B981' }}>
              {sending
                ? `Invio in corso... ${sendProgress != null ? `${sendProgress}%` : ''}`
                : '📤 Invia Rapportino'}
            </button>
          )}
        </div>
      </div>
    </div>
    </>
  )
}

// ─── Main Component ────────────────────────────────────────────────────────
export default function WorkOrderDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { user } = useAuth()
  const canOverrideStato = ['superadmin', 'dispatcher', 'responsabile_tecnico'].includes(user?.role || '')
  const isAdminOrSuperadmin = ['superadmin', 'admin'].includes(user?.role || '')
  const isTecnico = ['tecnico', 'tecnico_senior'].includes(user?.role ?? '')
  const isSuperadmin = user?.role === 'superadmin'
  const isReadonly = user?.role === 'readonly'
  const [showAssign, setShowAssign] = useState(false)
  const [tecnicoId, setTecnicoId] = useState('')
  const [showEseguito, setShowEseguito] = useState(false)
  const [statoNoteModal, setStatoNoteModal] = useState<{ stato: 'sospeso' | 'annullato' } | null>(null)
  const [statoNoteText, setStatoNoteText] = useState('')
  const [statoCausale, setStatoCausale] = useState('')
  const [statoFatturabile, setStatoFatturabile] = useState(false)
  const [editAppt, setEditAppt] = useState(false)
  const [apptDate, setApptDate] = useState('')
  const [apptOre, setApptOre] = useState('')
  const [editTipologia, setEditTipologia] = useState(false)
  const [tipologiaDraft, setTipologiaDraft] = useState('')
  const [editingField, setEditingField] = useState<string | null>(null)
  const [fieldDraft, setFieldDraft] = useState('')
  const [editAddress, setEditAddress] = useState(false)
  const [addrDraft, setAddrDraft] = useState({ indirizzo: '', numero_civico: '', comune: '', provincia: '', cap: '' })
  const [editGps, setEditGps] = useState(false)
  const [gpsDraft, setGpsDraft] = useState({ lat: '', lng: '' })
  const [editClienteNome, setEditClienteNome] = useState(false)
  const [clienteNomeDraft, setClienteNomeDraft] = useState({ nome: '', codice: '' })

  const { data: wo, isLoading } = useQuery({
    queryKey: ['work-order', id],
    queryFn: () => workOrdersApi.get(Number(id)).then(r => r.data),
  })

  // Orari apertura ufficio postale per il frazionario della pratica
  const apptFrazionario = wo?.codice_sito || ''
  const { data: orariAppt, isPending: orariApptPending } = useQuery({
    queryKey: ['orari-apertura', apptFrazionario, apptDate],
    queryFn: () => orariAperturaApi.getOrari(apptFrazionario, apptDate).then(r => r.data),
    enabled: editAppt && !!apptFrazionario && !!apptDate,
    retry: false,
  })
  const orariApptFuori = (() => {
    if (!orariAppt || orariAppt.chiuso || !apptOre || !orariAppt.ora_apertura || !orariAppt.ora_chiusura) return false
    const [oh, om] = apptOre.split(':').map(Number)
    const [ah, am] = orariAppt.ora_apertura.slice(0, 5).split(':').map(Number)
    const [ch, cm] = orariAppt.ora_chiusura.slice(0, 5).split(':').map(Number)
    return (oh * 60 + om) < (ah * 60 + am) || (oh * 60 + om) > (ch * 60 + cm)
  })()

  const { data: tecniciData } = useQuery({
    queryKey: ['tecnici-select'],
    queryFn: () => tecniciApi.list({ per_page: 100, is_active: true }).then(r => r.data.items),
  })

  // Listino del committente (per tipologia)
  const { data: listinoData } = useQuery({
    queryKey: ['listino', wo?.committente_id],
    queryFn: () => committentiApi.getListino(wo!.committente_id).then(r => r.data),
    enabled: !!wo?.committente_id,
  })

  const updateStato = useMutation({
    mutationFn: async ({ stato, note, causale, fatturabileManuale }: { stato: string; note?: string; causale?: string; fatturabileManuale?: boolean }) => {
      await workOrdersApi.updateStato(Number(id), STATO_TO_BACKEND[stato] || stato, note, fatturabileManuale)
      // Rapportino senza obbligo di foto/materiali, per sospensioni/annullamenti
      // decisi da superadmin/admin con causale — così l'intervento resta
      // documentato anche senza un intervento tecnico completo sul campo.
      if (causale) {
        const noteText = `PRATICA ${stato === 'sospeso' ? 'SOSPESA' : 'ANNULLATA'}\n\nMotivo: ${note || '—'}`
        const existing = await rapportiniApi.list({ work_order_id: Number(id), per_page: 1 })
        const items: any[] = existing.data?.items ?? []
        if (items.length > 0) {
          await rapportiniApi.update(items[0].id, {
            note_aggiuntive: noteText,
            esito: 'non_completato',
            causale_non_completamento: causale,
          })
        } else {
          await rapportiniApi.create({
            work_order_id: Number(id),
            data_intervento: format(new Date(), 'yyyy-MM-dd'),
            esito: 'non_completato',
            causale_non_completamento: causale,
            note_aggiuntive: noteText,
          })
        }
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['work-order', id] })
      toast.success('Stato aggiornato')
      setStatoNoteModal(null)
      setStatoNoteText('')
      setStatoCausale('')
      setStatoFatturabile(false)
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Errore aggiornamento stato'),
  })

  const assegna = useMutation({
    mutationFn: (data: { tecnico_id: number }) => workOrdersApi.assegna(Number(id), data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['work-order', id] })
      setShowAssign(false)
      toast.success('Tecnico assegnato!')
    },
  })

  const notifyWA = useMutation({
    mutationFn: () => workOrdersApi.notifyWhatsapp(Number(id)),
    onSuccess: () => toast.success('WhatsApp inviato al tecnico'),
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Tecnico senza numero WhatsApp'),
  })

  const saveAppt = useMutation({
    mutationFn: () => workOrdersApi.update(Number(id), {
      data_appuntamento: apptDate || null,
      ore_appuntamento: apptOre || null,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['work-order', id] })
      qc.invalidateQueries({ queryKey: ['work-orders'] })
      setEditAppt(false)
      toast.success('Appuntamento salvato')
    },
    onError: () => toast.error('Errore salvataggio'),
  })

  const saveTipologia = useMutation({
    mutationFn: () => workOrdersApi.update(Number(id), { tipologia: tipologiaDraft || null }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['work-order', id] })
      setEditTipologia(false)
      toast.success('Tipologia aggiornata')
    },
    onError: () => toast.error('Errore salvataggio'),
  })

  const saveField = useMutation({
    mutationFn: (field: string) => workOrdersApi.update(Number(id), { [field]: fieldDraft || null }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['work-order', id] })
      setEditingField(null)
      toast.success('Campo aggiornato')
    },
    onError: () => toast.error('Errore salvataggio'),
  })

  const saveAddress = useMutation({
    mutationFn: () => wo!.cliente_finale_id
      ? clientiFinaliApi.patchAddress(wo!.cliente_finale_id, {
          indirizzo: addrDraft.indirizzo || undefined,
          numero_civico: addrDraft.numero_civico || undefined,
          comune: addrDraft.comune || undefined,
          provincia: addrDraft.provincia || undefined,
          cap: addrDraft.cap || undefined,
        })
      : workOrdersApi.update(Number(id), {
          cliente_indirizzo: [addrDraft.indirizzo, addrDraft.numero_civico].filter(Boolean).join(' ') || undefined,
          cliente_comune: addrDraft.comune || undefined,
          cliente_provincia: addrDraft.provincia || undefined,
          cliente_cap: addrDraft.cap || undefined,
        }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['work-order', id] })
      setEditAddress(false)
      toast.success('Indirizzo aggiornato')
    },
    onError: () => toast.error('Errore salvataggio'),
  })

  const saveGps = useMutation({
    mutationFn: () => wo!.cliente_finale_id
      ? clientiFinaliApi.patchAddress(wo!.cliente_finale_id, {
          lat: gpsDraft.lat !== '' ? parseFloat(gpsDraft.lat) : undefined,
          lng: gpsDraft.lng !== '' ? parseFloat(gpsDraft.lng) : undefined,
        })
      : workOrdersApi.update(Number(id), {
          lat: gpsDraft.lat !== '' ? parseFloat(gpsDraft.lat) : undefined,
          lng: gpsDraft.lng !== '' ? parseFloat(gpsDraft.lng) : undefined,
        }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['work-order', id] })
      setEditGps(false)
      toast.success('Coordinate GPS aggiornate')
    },
    onError: () => toast.error('Errore salvataggio'),
  })

  const autoGps = useMutation({
    mutationFn: () => workOrdersApi.geocode(Number(id)),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['work-order', id] })
      setGpsDraft({ lat: String(r.data.lat), lng: String(r.data.lng) })
      toast.success('Coordinate GPS trovate')
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Impossibile trovare le coordinate GPS'),
  })

  const saveClienteNome = useMutation({
    mutationFn: () => wo!.cliente_finale_id
      ? clientiFinaliApi.patchAddress(wo!.cliente_finale_id, {
          denominazione_sito: clienteNomeDraft.nome || undefined,
          codice_sito: clienteNomeDraft.codice || undefined,
        })
      : workOrdersApi.update(Number(id), {
          denominazione_sito: clienteNomeDraft.nome || undefined,
          codice_sito: clienteNomeDraft.codice || undefined,
        }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['work-order', id] })
      setEditClienteNome(false)
      toast.success('Sito aggiornato')
    },
    onError: () => toast.error('Errore salvataggio'),
  })

  const handleDownloadPdf = async () => {
    if (!wo) return
    try {
      const r = await rapportiniApi.list({ per_page: 100 })
      const raps = r.data.items?.filter((x: any) => x.work_order_id === wo.id)
      if (!raps?.length) { toast.error('Nessun rapportino disponibile'); return }
      const pdfRes = await rapportiniApi.getPdf(raps[0].id)
      const url = URL.createObjectURL(pdfRes.data)
      const a = document.createElement('a')
      a.href = url; a.download = `rapportino_${wo.numero_progressivo}.pdf`; a.click()
      URL.revokeObjectURL(url)
    } catch { toast.error('PDF non disponibile') }
  }

  if (isLoading) return (
    <div className="flex flex-col items-center justify-center h-64 gap-3">
      <div className="w-8 h-8 rounded-full animate-spin" style={{ border: '2px solid var(--border-bright)', borderTopColor: 'var(--signal)' }} />
    </div>
  )
  if (!wo) return (
    <div className="flex flex-col items-center justify-center h-64 gap-2">
      <span style={{ color: 'var(--text-500)', fontSize: 14 }}>Work Order non trovato</span>
      <button onClick={() => navigate('/pratiche')} className="btn-secondary text-sm">Torna alle Pratiche</button>
    </div>
  )

  const statoUI = STATO_FROM_BACKEND[wo.stato] || wo.stato
  const isEseguito = statoUI === 'eseguito'
  const isTerminato = isReadonly || ((wo.stato === 'annullato' || isEseguito) && !canOverrideStato)

  return (
    <div className="space-y-5 max-w-5xl animate-fade-up">

      {/* Header */}
      <div className="flex items-start gap-4">
        <button onClick={() => navigate('/pratiche')} className="btn-secondary mt-0.5 px-2.5">
          <ArrowLeft size={16} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-100)', letterSpacing: '-0.02em' }}>
              {wo.numero_progressivo}
            </h1>
            {isSuperadmin && editingField === 'priorita' ? (
              <div className="flex items-center gap-1">
                <select
                  className="select !py-0.5 !text-xs !h-7"
                  value={fieldDraft}
                  onChange={e => setFieldDraft(e.target.value)}
                  autoFocus
                >
                  <option value="normale">Normale</option>
                  <option value="alta">Alta</option>
                  <option value="urgente">Urgente</option>
                  <option value="critica">Critica</option>
                </select>
                <button onClick={() => saveField.mutate('priorita')} disabled={saveField.isPending} className="btn-primary text-xs py-0.5 px-2">✓</button>
                <button onClick={() => setEditingField(null)} className="btn-secondary text-xs py-0.5 px-2">✕</button>
              </div>
            ) : (
              <span className="flex items-center gap-1">
                <PrioritaBadge priorita={wo.priorita} />
                {isSuperadmin && (
                  <button onClick={() => { setFieldDraft(wo.priorita || 'normale'); setEditingField('priorita') }} className="btn-ghost p-0.5" title="Modifica priorità">
                    <Pencil size={10} style={{ color: 'var(--text-500)' }} />
                  </button>
                )}
              </span>
            )}
            <StatoBadge stato={wo.stato} />
          </div>
          {isSuperadmin && editingField === 'titolo' ? (
            <div className="flex items-center gap-2 mt-1">
              <input
                className="input !py-1 !text-sm flex-1"
                value={fieldDraft}
                onChange={e => setFieldDraft(e.target.value)}
                autoFocus
                placeholder="Titolo pratica"
              />
              <button onClick={() => saveField.mutate('titolo')} disabled={saveField.isPending} className="btn-primary text-xs py-1 px-3">Salva</button>
              <button onClick={() => setEditingField(null)} className="btn-secondary text-xs py-1 px-2">✕</button>
            </div>
          ) : (
            <p style={{ fontSize: 13, color: 'var(--text-400)', marginTop: 4 }} className="flex items-center gap-1.5">
              {wo.titolo || wo.cliente_nome}
              {isSuperadmin && (
                <button onClick={() => { setFieldDraft(wo.titolo || ''); setEditingField('titolo') }} className="btn-ghost p-0.5" title="Modifica titolo">
                  <Pencil size={10} style={{ color: 'var(--text-500)' }} />
                </button>
              )}
            </p>
          )}
        </div>
        {isEseguito && (
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={handleDownloadPdf} className="btn-secondary flex items-center gap-2 text-sm">
              <Download size={14} /> PDF
            </button>
          </div>
        )}
      </div>

      {(wo.data_appuntamento || wo.ore_appuntamento || wo.slot_orario) && (
        <AppuntamentoChip wo={wo} />
      )}

      {/* Stato Workflow — 5 stati */}
      <div className="card p-5">
        <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--signal)', fontFamily: "'Barlow Semi Condensed', sans-serif", marginBottom: 14 }}>
          Stato Pratica
        </p>
        {isTecnico ? (
          !isTerminato ? (
            <button
              onClick={() => setShowEseguito(true)}
              style={{
                padding: '14px 24px', borderRadius: 10, fontSize: 15, fontWeight: 700,
                cursor: 'pointer', border: '2px solid #10B981',
                background: 'rgba(16,185,129,0.12)', color: '#10B981',
                width: '100%', textAlign: 'center' as const,
              }}
            >
              {'✓ Segna come Eseguito'}
            </button>
          ) : (
            <p style={{ fontSize: 13, color: 'var(--text-500)' }}>
              Pratica {wo.stato === 'annullato' ? 'annullata' : 'completata'}
            </p>
          )
        ) : (
          <div className="flex gap-2 flex-wrap">
            {STATI_UI.map(s => {
              const isCurrent = s.key === statoUI
              return (
                <button
                  key={s.key}
                  disabled={isTerminato && s.key !== statoUI || updateStato.isPending}
                  onClick={() => {
                    if (s.key === statoUI) return
                    if (s.key === 'eseguito') { setShowEseguito(true); return }
                    if (s.key === 'sospeso' || s.key === 'annullato') {
                      setStatoNoteText('')
                      setStatoCausale('')
                      setStatoFatturabile(false)
                      setStatoNoteModal({ stato: s.key })
                      return
                    }
                    updateStato.mutate({ stato: s.key })
                  }}
                  style={{
                    padding: '8px 18px', borderRadius: 8, fontSize: 13, fontWeight: isCurrent ? 700 : 500,
                    cursor: isTerminato && s.key !== statoUI ? 'not-allowed' : 'pointer',
                    opacity: isTerminato && s.key !== statoUI ? 0.35 : 1,
                    border: isCurrent ? `2px solid ${s.color}` : '1.5px solid var(--border-bright)',
                    background: isCurrent ? `${s.color}18` : 'var(--ink-600)',
                    color: isCurrent ? s.color : 'var(--text-400)',
                    boxShadow: isCurrent ? `0 0 10px ${s.color}30` : 'none',
                    transition: 'all 0.2s',
                  }}
                >
                  {s.key === 'eseguito' && !isCurrent ? '▶ ' : ''}{s.label}
                </button>
              )
            })}
          </div>
        )}
      </div>

      {/* Modale nota obbligatoria per sospensione/annullamento */}
      {statoNoteModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 440, boxShadow: '0 8px 32px rgba(0,0,0,0.18)', padding: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
              {statoNoteModal.stato === 'sospeso'
                ? <PauseCircle size={22} color="#F59E0B" />
                : <XCircle size={22} color="#EF4444" />}
              <span style={{ fontWeight: 700, fontSize: 17 }}>
                {statoNoteModal.stato === 'sospeso' ? 'Sospendi pratica' : 'Annulla pratica'}
              </span>
              <button onClick={() => setStatoNoteModal(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}>✕</button>
            </div>
            <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 18 }}>{wo.numero_progressivo} — {wo.titolo || wo.cliente_nome}</p>

            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Note interne <span style={{ color: '#dc2626' }}>*</span></label>
            <textarea
              rows={4}
              autoFocus
              value={statoNoteText}
              onChange={e => setStatoNoteText(e.target.value)}
              placeholder={`Descrivere il motivo del ${statoNoteModal.stato === 'sospeso' ? 'sospensione' : 'annullamento'} (obbligatorio)...`}
              style={{ width: '100%', borderRadius: 8, border: '1.5px solid #d1d5db', padding: '8px 10px', fontSize: 14, resize: 'vertical', boxSizing: 'border-box', marginBottom: isAdminOrSuperadmin ? 14 : 20 }}
            />

            {isAdminOrSuperadmin && (
              <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: 14, marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                  Genera rapportino (causale)
                </label>
                <select
                  value={statoCausale}
                  onChange={e => setStatoCausale(e.target.value)}
                  className="select"
                  style={{ width: '100%', marginBottom: 10 }}
                >
                  <option value="">Nessun rapportino</option>
                  <option value="cliente_assente">Cliente assente</option>
                  <option value="linea_non_attiva">Linea non attiva</option>
                  <option value="materiale_mancante">Materiale mancante</option>
                  <option value="accesso_negato">Accesso negato</option>
                  <option value="infrastruttura_non_conforme">Infrastruttura non conforme</option>
                  <option value="meteo_forza_maggiore">Meteo / forza maggiore</option>
                  <option value="problema_tecnico">Problema tecnico</option>
                  <option value="altro">Altro</option>
                </select>
                {statoCausale && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                    <input type="checkbox" checked={statoFatturabile} onChange={e => setStatoFatturabile(e.target.checked)} />
                    Includi in fatturazione
                  </label>
                )}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => setStatoNoteModal(null)}
                style={{ flex: 1, minHeight: 44, borderRadius: 10, background: '#f3f4f6', color: '#374151', fontWeight: 600, fontSize: 14, border: 'none', cursor: 'pointer' }}
              >Annulla</button>
              <button
                onClick={() => {
                  if (!statoNoteText.trim()) { toast.error('Le note interne sono obbligatorie'); return }
                  updateStato.mutate({
                    stato: statoNoteModal.stato,
                    note: statoNoteText.trim(),
                    causale: statoCausale || undefined,
                    fatturabileManuale: statoCausale ? statoFatturabile : undefined,
                  })
                }}
                disabled={updateStato.isPending || !statoNoteText.trim()}
                style={{
                  flex: 2, minHeight: 44, borderRadius: 10,
                  background: statoNoteModal.stato === 'sospeso' ? '#f59e0b' : '#dc2626',
                  color: '#fff', fontWeight: 700, fontSize: 15, border: 'none',
                  cursor: !statoNoteText.trim() ? 'not-allowed' : 'pointer',
                  opacity: updateStato.isPending || !statoNoteText.trim() ? 0.5 : 1,
                }}
              >
                {updateStato.isPending ? 'Salvataggio...' : 'Confermo'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">

          {/* Dettagli */}
          <div className="card p-5">
            <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--signal)', fontFamily: "'Barlow Semi Condensed', sans-serif", marginBottom: 16 }}>
              Dettagli Intervento
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5">
              <DetailRow label="Tipologia">
                {editTipologia ? (
                  <div className="flex flex-wrap items-center gap-2 mt-1">
                    <select
                      className="select !py-1 !text-xs flex-1 min-w-0"
                      value={tipologiaDraft}
                      onChange={e => setTipologiaDraft(e.target.value)}
                      autoFocus
                    >
                      <option value="">— nessuna —</option>
                      {(listinoData as any[] ?? [])
                        .filter((v: any) => v.is_active !== false)
                        .map((v: any) => (
                          <option key={v.id} value={v.descrizione}>
                            {v.descrizione}{!isTecnico && v.netto > 0 ? ` — €${v.netto}` : ''}
                          </option>
                        ))}
                    </select>
                    <button
                      onClick={() => saveTipologia.mutate()}
                      disabled={saveTipologia.isPending}
                      className="btn-primary text-xs py-1 px-3"
                    >
                      {saveTipologia.isPending ? '...' : 'Salva'}
                    </button>
                    <button onClick={() => setEditTipologia(false)} className="btn-secondary text-xs py-1 px-2">✕</button>
                  </div>
                ) : (
                  <span className="flex items-center gap-1.5">
                    <TipologiaBadge tipologia={wo.tipologia} />
                    {!isTerminato && !isTecnico && (
                      <button
                        onClick={() => { setTipologiaDraft(wo.tipologia || ''); setEditTipologia(true) }}
                        className="btn-ghost p-0.5 ml-1"
                        title="Modifica tipologia"
                      >
                        <Pencil size={11} style={{ color: 'var(--text-500)' }} />
                      </button>
                    )}
                  </span>
                )}
              </DetailRow>
              <DetailRow label="Committente"><span style={{ fontWeight: 500 }}>{wo.committente_nome}</span></DetailRow>
              <DetailRow label="N° Ordine">
                {isSuperadmin && editingField === 'numero_ordine_committente' ? (
                  <div className="flex items-center gap-2 mt-1">
                    <input className="input !py-1 !text-xs flex-1" value={fieldDraft} onChange={e => setFieldDraft(e.target.value)} autoFocus />
                    <button onClick={() => saveField.mutate('numero_ordine_committente')} disabled={saveField.isPending} className="btn-primary text-xs py-1 px-2">✓</button>
                    <button onClick={() => setEditingField(null)} className="btn-secondary text-xs py-1 px-2">✕</button>
                  </div>
                ) : (
                  <span className="flex items-center gap-1.5" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12 }}>
                    {wo.numero_ordine_committente || '—'}
                    {isSuperadmin && (
                      <button onClick={() => { setFieldDraft(wo.numero_ordine_committente || ''); setEditingField('numero_ordine_committente') }} className="btn-ghost p-0.5" title="Modifica N° Ordine">
                        <Pencil size={10} style={{ color: 'var(--text-500)' }} />
                      </button>
                    )}
                  </span>
                )}
              </DetailRow>
              <DetailRow label="Commessa">
                {isSuperadmin && editingField === 'numero_commessa' ? (
                  <div className="flex items-center gap-2 mt-1">
                    <input className="input !py-1 !text-xs flex-1" value={fieldDraft} onChange={e => setFieldDraft(e.target.value)} autoFocus />
                    <button onClick={() => saveField.mutate('numero_commessa')} disabled={saveField.isPending} className="btn-primary text-xs py-1 px-2">✓</button>
                    <button onClick={() => setEditingField(null)} className="btn-secondary text-xs py-1 px-2">✕</button>
                  </div>
                ) : (
                  <span className="flex items-center gap-1.5" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12 }}>
                    {wo.numero_commessa || '—'}
                    {isSuperadmin && (
                      <button onClick={() => { setFieldDraft(wo.numero_commessa || ''); setEditingField('numero_commessa') }} className="btn-ghost p-0.5" title="Modifica Commessa">
                        <Pencil size={10} style={{ color: 'var(--text-500)' }} />
                      </button>
                    )}
                  </span>
                )}
              </DetailRow>
              <DetailRow label="Appuntamento">
                {editAppt ? (
                  <div className="mt-1 space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <input type="date" className="input !py-1 !text-xs w-36"
                        value={apptDate}
                        onChange={e => setApptDate(e.target.value)}
                      />
                      <input type="time" className="input !py-1 !text-xs w-28"
                        value={apptOre}
                        onChange={e => setApptOre(e.target.value)}
                        placeholder="HH:MM"
                      />
                      <button onClick={() => saveAppt.mutate()} disabled={saveAppt.isPending} className="btn-primary text-xs py-1 px-3">
                        {saveAppt.isPending ? '...' : 'Salva'}
                      </button>
                      <button onClick={() => setEditAppt(false)} className="btn-secondary text-xs py-1 px-2">✕</button>
                    </div>
                    {apptFrazionario && apptDate && (orariApptPending || orariAppt != null) && (
                      <div className={`rounded-lg px-3 py-2 text-xs ${
                        orariApptPending ? 'bg-slate-50 text-slate-400' :
                        orariAppt!.chiuso ? 'bg-red-50 border border-red-200 text-red-700' :
                        'bg-blue-50 border border-blue-200 text-blue-800'
                      }`}>
                        {orariApptPending
                          ? 'Verifica orari ufficio...'
                          : orariAppt!.chiuso
                          ? `⚠️ Ufficio ${apptFrazionario} chiuso il ${format(new Date(apptDate + 'T00:00:00'), 'dd/MM/yyyy', { locale: it })}`
                          : <>
                              📍 <strong>{apptFrazionario}</strong> — {format(new Date(apptDate + 'T00:00:00'), 'EEEE d MMM yyyy', { locale: it })}{' '}
                              🕐 <strong>{orariAppt!.ora_apertura?.slice(0, 5)} – {orariAppt!.ora_chiusura?.slice(0, 5)}</strong>
                            </>
                        }
                        {orariApptFuori && (
                          <div className="mt-1 font-semibold text-red-600">
                            ⚠ Ora {apptOre} fuori dall'orario di apertura ({orariAppt?.ora_apertura?.slice(0,5)}–{orariAppt?.ora_chiusura?.slice(0,5)})
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <span className="flex items-center gap-1.5">
                    <Calendar size={12} style={{ color: 'var(--text-500)' }} />
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12 }}>
                      {wo.data_appuntamento ? format(new Date(wo.data_appuntamento), 'dd MMM yyyy', { locale: it }) : '—'}
                    </span>
                    {(wo.ore_appuntamento || wo.slot_orario) && (
                      <span className="flex items-center gap-1" style={{ fontSize: 11, color: 'var(--text-400)', fontFamily: "'JetBrains Mono', monospace" }}>
                        <Clock size={10} />{wo.ore_appuntamento || wo.slot_orario}
                      </span>
                    )}
                    {!isTecnico && (
                    <button
                      onClick={() => { setApptDate(wo.data_appuntamento ? wo.data_appuntamento.slice(0, 10) : ''); setApptOre(wo.ore_appuntamento || ''); setEditAppt(true) }}
                      className="btn-ghost p-0.5 ml-1" title="Modifica appuntamento"
                    >
                      <Pencil size={11} style={{ color: 'var(--text-500)' }} />
                    </button>
                    )}
                  </span>
                )}
              </DetailRow>
              <DetailRow label="Parametri Rete">
                <div className="text-xs font-mono space-y-0.5" style={{ color: 'var(--text-400)' }}>
                  {wo.loopback_mgmt && <div>MGMT: {wo.loopback_mgmt}</div>}
                  {wo.loopback_vpn && <div>VPN: {wo.loopback_vpn}</div>}
                  {wo.p2p_primario_444 && <div>P2P 444: {wo.p2p_primario_444}</div>}
                  {wo.p2p_backup_445 && <div>P2P 445: {wo.p2p_backup_445}</div>}
                  {!wo.loopback_mgmt && !wo.loopback_vpn && '—'}
                </div>
              </DetailRow>
            </div>

            {(wo.descrizione || isSuperadmin) && (
              <div style={{ marginTop: 20, paddingTop: 20, borderTop: '1px solid var(--border)' }}>
                <p style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--text-500)', fontFamily: "'Barlow Semi Condensed', sans-serif", marginBottom: 6 }} className="flex items-center gap-1.5">
                  Descrizione
                  {isSuperadmin && editingField !== 'descrizione' && (
                    <button onClick={() => { setFieldDraft(wo.descrizione || ''); setEditingField('descrizione') }} className="btn-ghost p-0.5" title="Modifica descrizione">
                      <Pencil size={10} style={{ color: 'var(--text-500)' }} />
                    </button>
                  )}
                </p>
                {isSuperadmin && editingField === 'descrizione' ? (
                  <div className="space-y-2">
                    <textarea
                      className="input !py-1 !text-xs w-full"
                      rows={4}
                      value={fieldDraft}
                      onChange={e => setFieldDraft(e.target.value)}
                      autoFocus
                      placeholder="Descrizione intervento"
                    />
                    <div className="flex gap-2">
                      <button onClick={() => saveField.mutate('descrizione')} disabled={saveField.isPending} className="btn-primary text-xs py-1 px-3">Salva</button>
                      <button onClick={() => setEditingField(null)} className="btn-secondary text-xs py-1 px-2">Annulla</button>
                    </div>
                  </div>
                ) : (
                  <p style={{ fontSize: 13, color: 'var(--text-300)', lineHeight: 1.6 }}>{wo.descrizione || <span style={{ color: 'var(--text-600)', fontStyle: 'italic' }}>Nessuna descrizione</span>}</p>
                )}
              </div>
            )}

            {isSuperadmin && (
              <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
                <p style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--text-500)', fontFamily: "'Barlow Semi Condensed', sans-serif", marginBottom: 6 }} className="flex items-center gap-1.5">
                  Note Tecniche
                  {editingField !== 'note_tecniche' && (
                    <button onClick={() => { setFieldDraft(wo.note_tecniche || ''); setEditingField('note_tecniche') }} className="btn-ghost p-0.5" title="Modifica note tecniche">
                      <Pencil size={10} style={{ color: 'var(--text-500)' }} />
                    </button>
                  )}
                </p>
                {editingField === 'note_tecniche' ? (
                  <div className="space-y-2">
                    <textarea className="input !py-1 !text-xs w-full" rows={3} value={fieldDraft} onChange={e => setFieldDraft(e.target.value)} autoFocus placeholder="Note tecniche interne" />
                    <div className="flex gap-2">
                      <button onClick={() => saveField.mutate('note_tecniche')} disabled={saveField.isPending} className="btn-primary text-xs py-1 px-3">Salva</button>
                      <button onClick={() => setEditingField(null)} className="btn-secondary text-xs py-1 px-2">Annulla</button>
                    </div>
                  </div>
                ) : (
                  <p style={{ fontSize: 13, color: 'var(--text-300)', lineHeight: 1.6 }}>{wo.note_tecniche || <span style={{ color: 'var(--text-600)', fontStyle: 'italic' }}>Nessuna nota tecnica</span>}</p>
                )}
              </div>
            )}

            {isSuperadmin && (
              <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
                <p style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--text-500)', fontFamily: "'Barlow Semi Condensed', sans-serif", marginBottom: 6 }} className="flex items-center gap-1.5">
                  Note Interne
                  {editingField !== 'note' && (
                    <button onClick={() => { setFieldDraft(wo.note || ''); setEditingField('note') }} className="btn-ghost p-0.5" title="Modifica note interne">
                      <Pencil size={10} style={{ color: 'var(--text-500)' }} />
                    </button>
                  )}
                </p>
                {editingField === 'note' ? (
                  <div className="space-y-2">
                    <textarea className="input !py-1 !text-xs w-full" rows={3} value={fieldDraft} onChange={e => setFieldDraft(e.target.value)} autoFocus placeholder="Note interne pratica" />
                    <div className="flex gap-2">
                      <button onClick={() => saveField.mutate('note')} disabled={saveField.isPending} className="btn-primary text-xs py-1 px-3">Salva</button>
                      <button onClick={() => setEditingField(null)} className="btn-secondary text-xs py-1 px-2">Annulla</button>
                    </div>
                  </div>
                ) : (
                  <p style={{ fontSize: 13, color: 'var(--text-300)', lineHeight: 1.6 }}>{wo.note || <span style={{ color: 'var(--text-600)', fontStyle: 'italic' }}>Nessuna nota interna</span>}</p>
                )}
              </div>
            )}
          </div>

          {/* Storico */}
          {!isTecnico && wo.storico_stati?.length > 0 && (
            <div className="card p-5">
              <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--signal)', fontFamily: "'Barlow Semi Condensed', sans-serif", marginBottom: 16 }}>
                Storico Stato
              </p>
              <div className="space-y-4">
                {[...wo.storico_stati].reverse().map((s: any, idx: number) => (
                  <div key={idx} style={{ display: 'flex', gap: 12 }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, marginTop: 4, background: idx === 0 ? 'var(--signal)' : 'var(--ink-400)', border: '1px solid var(--border-bright)' }} />
                    <div>
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span style={{ fontSize: 13, fontWeight: 600, color: idx === 0 ? 'var(--text-100)' : 'var(--text-300)', textTransform: 'capitalize' }}>
                          {s.stato?.replace(/_/g, ' ')}
                        </span>
                        <span style={{ fontSize: 11, color: 'var(--text-500)' }}>{s.utente}</span>
                      </div>
                      <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: 'var(--text-600)', marginTop: 2 }}>
                        {s.timestamp ? format(new Date(s.timestamp), 'dd/MM/yyyy HH:mm', { locale: it }) : ''}
                        {s.tecnico_nome && ` · Tecnico assegnato: ${s.tecnico_nome}`}
                      </div>
                      {s.note && <p style={{ fontSize: 12, color: 'var(--text-500)', marginTop: 3, fontStyle: 'italic' }}>{s.note}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <SideCard title="Cliente / Sito">
            <div className="space-y-2.5">
              <div className="flex items-start gap-2">
                <User size={13} style={{ color: 'var(--text-500)', marginTop: 1, flexShrink: 0 }} />
                {isSuperadmin && editClienteNome ? (
                  <div className="flex-1 space-y-1.5">
                    <input
                      className="input !py-1 !text-xs w-full"
                      placeholder="Nome sito (es. POTENZA)"
                      value={clienteNomeDraft.nome}
                      onChange={e => setClienteNomeDraft(p => ({ ...p, nome: e.target.value }))}
                      autoFocus
                    />
                    <input
                      className="input !py-1 !text-xs w-full"
                      placeholder="Codice sito (es. 51291)"
                      value={clienteNomeDraft.codice}
                      onChange={e => setClienteNomeDraft(p => ({ ...p, codice: e.target.value }))}
                    />
                    <div className="flex gap-1">
                      <button onClick={() => saveClienteNome.mutate()} disabled={saveClienteNome.isPending} className="btn-primary text-xs py-1 px-3">Salva</button>
                      <button onClick={() => setEditClienteNome(false)} className="btn-secondary text-xs py-1 px-2">Annulla</button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-1 flex-wrap">
                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-200)' }}>{wo.cliente_nome}</span>
                    {wo.codice_sito && <span style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--text-500)' }}>[{wo.codice_sito}]</span>}
                    {(wo.flag_vip || wo.flag_critico) && (
                      <span>
                        {wo.flag_vip && <span title="VIP" style={{ color: '#f59e0b', fontSize: 11 }}>★</span>}
                        {wo.flag_critico && <span title="Critico" style={{ color: '#ef4444', fontSize: 11, marginLeft: 2 }}>!</span>}
                      </span>
                    )}
                    {isSuperadmin && (
                      <button
                        onClick={() => { setClienteNomeDraft({ nome: wo.cliente_nome || '', codice: wo.codice_sito || '' }); setEditClienteNome(true) }}
                        className="btn-ghost p-0.5" title="Modifica nome sito"
                      >
                        <Pencil size={10} style={{ color: 'var(--text-500)' }} />
                      </button>
                    )}
                  </div>
                )}
              </div>
              {wo.cliente_tipologia_sito && (
                <div style={{ fontSize: 11, color: 'var(--text-500)', textTransform: 'capitalize', paddingLeft: 21 }}>
                  {wo.cliente_tipologia_sito.replace(/_/g, ' ')}
                </div>
              )}
              {(wo.cliente_indirizzo || isSuperadmin) && (
                <div className="flex items-start gap-2">
                  <MapPin size={13} style={{ color: 'var(--text-500)', marginTop: 1, flexShrink: 0 }} />
                  {isSuperadmin && editAddress ? (
                    <div className="flex-1 space-y-1.5">
                      <input className="input !py-1 !text-xs w-full" placeholder="Indirizzo" value={addrDraft.indirizzo} onChange={e => setAddrDraft(p => ({ ...p, indirizzo: e.target.value }))} />
                      <div className="flex gap-1">
                        <input className="input !py-1 !text-xs w-14" placeholder="N°" value={addrDraft.numero_civico} onChange={e => setAddrDraft(p => ({ ...p, numero_civico: e.target.value }))} />
                        <input className="input !py-1 !text-xs flex-1" placeholder="Comune" value={addrDraft.comune} onChange={e => setAddrDraft(p => ({ ...p, comune: e.target.value }))} />
                      </div>
                      <div className="flex gap-1">
                        <input className="input !py-1 !text-xs w-16" placeholder="Prov." value={addrDraft.provincia} onChange={e => setAddrDraft(p => ({ ...p, provincia: e.target.value }))} />
                        <input className="input !py-1 !text-xs w-20" placeholder="CAP" value={addrDraft.cap} onChange={e => setAddrDraft(p => ({ ...p, cap: e.target.value }))} />
                      </div>
                      <div className="flex gap-1 mt-1">
                        <button onClick={() => saveAddress.mutate()} disabled={saveAddress.isPending} className="btn-primary text-xs py-1 px-3">Salva</button>
                        <button onClick={() => setEditAddress(false)} className="btn-secondary text-xs py-1 px-2">Annulla</button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, color: 'var(--text-400)', lineHeight: 1.5 }}>
                      <div className="flex items-center gap-1">
                        {wo.cliente_indirizzo || <span style={{ color: 'var(--text-600)', fontStyle: 'italic' }}>Nessun indirizzo</span>}
                        {isSuperadmin && (
                          <button onClick={() => {
                            const parts = (wo.cliente_indirizzo || '').split(',')
                            setAddrDraft({ indirizzo: parts[0]?.trim() || '', numero_civico: '', comune: parts[1]?.trim() || '', provincia: wo.cliente_provincia || '', cap: wo.cliente_cap || '' })
                            setEditAddress(true)
                          }} className="btn-ghost p-0.5" title="Modifica indirizzo">
                            <Pencil size={10} style={{ color: 'var(--text-500)' }} />
                          </button>
                        )}
                      </div>
                      {(wo.cliente_interno || wo.cliente_piano) && (
                        <div style={{ fontSize: 11 }}>
                          {wo.cliente_piano && `Piano ${wo.cliente_piano}`}
                          {wo.cliente_piano && wo.cliente_interno && ' · '}
                          {wo.cliente_interno && `Int. ${wo.cliente_interno}`}
                        </div>
                      )}
                      {wo.cliente_regione && <div style={{ fontSize: 11 }}>{wo.cliente_regione}</div>}
                    </div>
                  )}
                </div>
              )}
              {wo.cliente_telefono && (
                <div className="flex items-center gap-2">
                  <span style={{ fontSize: 12 }}>📞</span>
                  <a href={`tel:${wo.cliente_telefono}`} style={{ fontSize: 12, color: 'var(--signal)' }}>{wo.cliente_telefono}</a>
                </div>
              )}
              {wo.cliente_email && (
                <div className="flex items-center gap-2">
                  <span style={{ fontSize: 12 }}>✉</span>
                  <a href={`mailto:${wo.cliente_email}`} style={{ fontSize: 11, color: 'var(--signal)', wordBreak: 'break-all' }}>{wo.cliente_email}</a>
                </div>
              )}
              {wo.referente_locale && (
                <div style={{ paddingTop: 8, borderTop: '1px solid var(--border)', marginTop: 4 }}>
                  <p style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--text-500)', marginBottom: 4 }}>Referente Locale</p>
                  <p style={{ fontSize: 12, color: 'var(--text-300)' }}>{wo.referente_locale}</p>
                  {wo.telefono_locale && (
                    <a href={`tel:${wo.telefono_locale}`} style={{ fontSize: 12, color: 'var(--signal)' }}>📞 {wo.telefono_locale}</a>
                  )}
                  {wo.orari_accessibilita && (
                    <p style={{ fontSize: 11, color: 'var(--text-500)', marginTop: 2 }}>🕐 {wo.orari_accessibilita}</p>
                  )}
                </div>
              )}
              {wo.dettagli_accesso && Object.values(wo.dettagli_accesso).some(Boolean) && (
                <div style={{ paddingTop: 8, borderTop: '1px solid var(--border)', marginTop: 4 }}>
                  <p style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--text-500)', marginBottom: 4 }}>Accesso</p>
                  <div style={{ fontSize: 11, color: 'var(--text-400)', lineHeight: 1.8 }}>
                    {wo.dettagli_accesso.portineria && <div>🏢 Portineria</div>}
                    {wo.dettagli_accesso.codice_cancello && <div>🔑 Codice: {wo.dettagli_accesso.codice_cancello}</div>}
                    {wo.dettagli_accesso.ascensore && <div>🛗 Ascensore</div>}
                    {wo.dettagli_accesso.ztl && <div>⚠ ZTL</div>}
                    {wo.dettagli_accesso.parcheggio && <div>🅿 {wo.dettagli_accesso.parcheggio}</div>}
                    {wo.dettagli_accesso.note_accesso && <div style={{ fontStyle: 'italic' }}>{wo.dettagli_accesso.note_accesso}</div>}
                  </div>
                </div>
              )}
              {wo.note_operative && (
                <div style={{ paddingTop: 8, borderTop: '1px solid var(--border)', marginTop: 4 }}>
                  <p style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--text-500)', marginBottom: 4 }}>Note Operative</p>
                  <p style={{ fontSize: 11, color: 'var(--text-400)', lineHeight: 1.6 }}>{wo.note_operative}</p>
                </div>
              )}
              {(wo.cliente_lat && wo.cliente_lng || isSuperadmin) && (
                <div style={{ paddingTop: 8, borderTop: '1px solid var(--border)', marginTop: 4 }}>
                  {isSuperadmin && editGps ? (
                    <div className="space-y-1.5">
                      <p style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--text-500)', marginBottom: 4 }}>GPS</p>
                      <div className="flex gap-1">
                        <input className="input !py-1 !text-xs flex-1" placeholder="Latitudine (es. 45.4654)" value={gpsDraft.lat} onChange={e => setGpsDraft(p => ({ ...p, lat: e.target.value }))} />
                        <input className="input !py-1 !text-xs flex-1" placeholder="Longitudine (es. 9.1859)" value={gpsDraft.lng} onChange={e => setGpsDraft(p => ({ ...p, lng: e.target.value }))} />
                      </div>
                      <div className="flex gap-1 mt-1">
                        <button onClick={() => saveGps.mutate()} disabled={saveGps.isPending} className="btn-primary text-xs py-1 px-3">Salva</button>
                        <button onClick={() => setEditGps(false)} className="btn-secondary text-xs py-1 px-2">Annulla</button>
                        <button onClick={() => autoGps.mutate()} disabled={autoGps.isPending} className="btn-secondary text-xs py-1 px-2" title="Trova automaticamente le coordinate dall'indirizzo">
                          📍 {autoGps.isPending ? 'Ricerca…' : 'Trova GPS'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      {wo.cliente_lat && wo.cliente_lng ? (
                        <a
                          href={`https://www.google.com/maps?q=${encodeURIComponent(
                            [wo.cliente_indirizzo, wo.cliente_comune, wo.cliente_provincia].filter(Boolean).join(', ')
                              || `${wo.cliente_lat},${wo.cliente_lng}`
                          )}`}
                          target="_blank" rel="noreferrer"
                          style={{ fontSize: 12, color: 'var(--signal)' }}
                        >
                          🗺 Maps ({wo.cliente_lat.toFixed(4)}, {wo.cliente_lng.toFixed(4)})
                        </a>
                      ) : (
                        <span style={{ fontSize: 11, color: 'var(--text-600)', fontStyle: 'italic' }}>Coordinate assenti</span>
                      )}
                      {isSuperadmin && (
                        <button onClick={() => { setGpsDraft({ lat: wo.cliente_lat?.toString() || '', lng: wo.cliente_lng?.toString() || '' }); setEditGps(true) }} className="btn-ghost p-0.5" title="Modifica GPS">
                          <Pencil size={10} style={{ color: 'var(--text-500)' }} />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </SideCard>

          <SideCard title="Tecnico Assegnato">
            <div className="flex items-center justify-between mb-3">
              <div>
                {wo.tecnico_nome ? (
                  <div className="flex items-center gap-2">
                    <div style={{ width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, var(--signal), #ea580c)', color: 'white', fontWeight: 700, fontSize: 12 }}>
                      {wo.tecnico_nome.split(' ').map((n: string) => n[0]).join('')}
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-200)' }}>{wo.tecnico_nome}</span>
                  </div>
                ) : (
                  <p style={{ fontSize: 12, color: 'var(--text-500)' }}>Nessun tecnico</p>
                )}
              </div>
              {!isTerminato && !isTecnico && (
                <button onClick={() => setShowAssign(!showAssign)} className="btn-ghost px-2 py-1" style={{ fontSize: 11 }}>
                  {wo.tecnico_nome ? 'Cambia' : 'Assegna'}
                </button>
              )}
            </div>
            {showAssign && (
              <div className="space-y-2">
                <select className="select text-sm" value={tecnicoId} onChange={e => setTecnicoId(e.target.value)}>
                  <option value="">Seleziona tecnico…</option>
                  {tecniciData?.map((t: any) => (
                    <option key={t.id} value={t.id}>{t.nome} {t.cognome} — {t.zona_primaria}</option>
                  ))}
                </select>
                <button className="btn-primary w-full text-xs" disabled={!tecnicoId || assegna.isPending}
                  onClick={() => assegna.mutate({ tecnico_id: Number(tecnicoId) })}>
                  Conferma
                </button>
              </div>
            )}
            {wo?.tecnico_id && !isTecnico && (
              <button
                onClick={() => notifyWA.mutate()}
                disabled={notifyWA.isPending}
                style={{
                  width: '100%', marginTop: 8, padding: '7px 12px',
                  background: notifyWA.isPending ? '#e5e7eb' : '#25D366',
                  color: notifyWA.isPending ? '#9ca3af' : '#fff',
                  border: 'none', borderRadius: 8, cursor: notifyWA.isPending ? 'not-allowed' : 'pointer',
                  fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.123.553 4.116 1.522 5.847L.057 23.776a.5.5 0 0 0 .624.624l5.929-1.465A11.945 11.945 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.893 0-3.667-.52-5.183-1.427l-.371-.22-3.518.87.885-3.427-.242-.386A9.937 9.937 0 0 1 2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/></svg>
                {notifyWA.isPending ? 'Invio…' : 'Invia WhatsApp al tecnico'}
              </button>
            )}
          </SideCard>

          {/* Azione rapida eseguito */}
          {!isTerminato && !isTecnico && (
            <SideCard title="Azione Rapida">
              <button
                onClick={() => setShowEseguito(true)}
                className="w-full flex items-center gap-2 px-3 py-3 rounded-xl text-sm font-semibold transition-all"
                style={{ background: 'rgba(16,185,129,0.12)', color: '#10B981', border: '1.5px solid rgba(16,185,129,0.3)' }}
              >
                <CheckCircle size={16} /> Segna come Eseguito
              </button>
            </SideCard>
          )}
        </div>
      </div>

      {/* Modal eseguito */}
      {showEseguito && (
        <EseguitoModal
          wo={wo}
          onClose={() => setShowEseguito(false)}
          onSuccess={() => {
            setShowEseguito(false)
            qc.invalidateQueries({ queryKey: ['work-order', id] })
            qc.invalidateQueries({ queryKey: ['work-orders'] })
            qc.invalidateQueries({ queryKey: ['mio-turno'] })
            toast.success('Pratica segnata come eseguita!')
            if (isTecnico) navigate('/mio-turno')
          }}
        />
      )}
    </div>
  )
}
